import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Plus, Wallet, RefreshCw, Trash2, CreditCard, BarChart3,
  Link2, Unlink, Home, Gem, Car, Search, ChevronDown, Pencil, Info
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  Line, XAxis, YAxis, Area, AreaChart,
  ReferenceLine, ReferenceDot, ComposedChart
} from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';
import {
  COLORS, ACCOUNT_TYPES, POS_TYPES, GEOS, SECTORS, RE_TYPES, ASSET_CATS,
  TICKER_DOMAIN_MAP, CRYPTO_MAP,
  INSTITUTIONS, BANQUES_FR, EPARGNE_SALARIALE_INSTITUTIONS,
  TYPES_EPARGNE_PERSONNELLE, TYPES_EPARGNE_SALARIALE,
  CRYPTO_PLATFORMS, ASSURANCE_VIE_PROVIDERS,
  BROKER_MAP, DEBT_TYPE_MAP,
} from './patrimoine/constants';
import { fmt, fmtD, fmtP, computeAmortization } from './patrimoine/utils';

const PatrimoinePage = () => {
  const { token, user } = useAuth();
  const userId = user?.id || 'anon';
  const ls = useMemo(() => ({
    get: (k, fb) => { try { const v = localStorage.getItem(`capos_${userId}_${k}`); return v ? JSON.parse(v) : fb; } catch { return fb; } },
    set: (k, v) => localStorage.setItem(`capos_${userId}_${k}`, JSON.stringify(v))
  }), [userId]);

  // ── State ──
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const hAuth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const hydratedRef = React.useRef(false);
  const saveTimerRef = React.useRef(null);
  const [storeLoaded, setStoreLoaded] = useState(false);

  const [tab, setTab] = useState('overview');
  const [accounts, setAccounts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [savings, setSavings] = useState([]);
  const [realEstate, setRealEstate] = useState([]);
  const [otherAssets, setOtherAssets] = useState([]);
  const [debts, setDebts] = useState([]);
  const [priceCache, setPriceCache] = useState(() => ls.get('ptr_prices', {}));
  const [fxCache, setFxCache] = useState(() => ls.get('ptr_fx_cache', {}));
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [accountFilter, setAccountFilter] = useState('all');
  const [posDetailOpen, setPosDetailOpen] = useState(false);
  const [selectedPos, setSelectedPos] = useState(null);
  const [projMonthly, setProjMonthly] = useState(500);
  const [projReturn, setProjReturn] = useState(7);
  const [overviewPeriod, setOverviewPeriod] = useState('5A');
  const [savLinks, setSavLinks] = useState({});
  const [objectives, setObjectives] = useState([]);
  const [logoCache, setLogoCache] = useState(() => ls.get('ptr_logo_cache', {}));

  // Dialogs
  const [acctDlg, setAcctDlg] = useState(false);
  const [posDlg, setPosDlg] = useState(false);
  const [savDlg, setSavDlg] = useState(false);
  const [debtDlg, setDebtDlg] = useState(false);
  const [prepayDlg, setPrepayDlg] = useState({ open: false, debt: null, amount: '', date: '' });
  // Allocation manager — split a savings account balance into named buckets, each optionally
  // linked to an objective. Total allocations must remain <= the account balance.
  const [allocDlg, setAllocDlg] = useState({ open: false, savingId: null, items: [] });
  const [txDlg, setTxDlg] = useState(false);
  const [reDlg, setReDlg] = useState(false);
  const [oaDlg, setOaDlg] = useState(false);
  const [editPosDlg, setEditPosDlg] = useState(false);
  const [editPosForm, setEditPosForm] = useState(null);
  const [deleteAcctConfirm, setDeleteAcctConfirm] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleResetPatrimoine = async () => {
    try {
      await axios.delete(`${API}/reset/patrimoine`, hAuth);
    } catch {}
    // Clear local price/logo caches (transient localStorage)
    const prefix = `capos_${userId}_ptr_`;
    Object.keys(localStorage).filter(k => k.startsWith(prefix)).forEach(k => localStorage.removeItem(k));
    setShowResetConfirm(false);
    toast.success('Données réinitialisées avec succès');
    window.location.reload();
  };

  // Forms
  const today = format(new Date(), 'yyyy-MM-dd');
  const emptyAcct = { name: '', type: 'PEA', broker: '' };
  const emptyPos = { name: '', ticker: '', isin: '', type: 'ETF', account: '', geo: 'Europe', sector: 'Tech', quantity: '', price: '', date: today };
  const emptySav = { category: 'personnelle', accountType: '', customType: '', name: '', institution: '', rate: '', balance: '', date: '', employerMatchPct: '' };
  const emptyDebt = { name: '', type: 'immobilier', total_amount: '', monthly_payment: '', interest_rate: '', start_date: '', ownership_pct: '' };
  const emptyTx = { type: 'buy', quantity: '', price: '', date: today };
  const emptyRE = { name: '', type: 'Résidence principale', value: '', purchase_price: '', date: '', linked_debt: '' };
  const emptyOA = { name: '', category: 'Autre', value: '', purchase_price: '', date: '' };
  const [acctForm, setAcctForm] = useState(emptyAcct);
  const [posForm, setPosForm] = useState(emptyPos);
  const [savForm, setSavForm] = useState(emptySav);
  const [debtForm, setDebtForm] = useState(emptyDebt);
  const [txForm, setTxForm] = useState(emptyTx);
  const [reForm, setReForm] = useState(emptyRE);
  const [oaForm, setOaForm] = useState(emptyOA);

  // ── Ticker autocomplete for the "Ajouter une position" form ──
  // Queries the existing /api/market/search proxy (Yahoo Finance) and lets the user
  // pick a match to pre-fill name + ticker. Active only on the add-position dialog.
  const [tickerQuery, setTickerQuery] = useState('');
  const [tickerResults, setTickerResults] = useState([]);
  const [tickerLoading, setTickerLoading] = useState(false);
  const [tickerOpen, setTickerOpen] = useState(false);
  useEffect(() => {
    const q = tickerQuery.trim();
    if (q.length < 3) { setTickerResults([]); setTickerLoading(false); return; }
    setTickerLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/market/search`, { params: { q }, headers: { Authorization: `Bearer ${token}` } });
        setTickerResults(Array.isArray(r.data?.results) ? r.data.results : []);
      } catch { setTickerResults([]); }
      finally { setTickerLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [tickerQuery, token]);

  // ── Persistence helpers (state only; autosave effect below PUTs to backend) ──
  const saveAccounts = (a) => { setAccounts(a); };
  const savePositions = (p) => { setPositions(p); };
  const saveSavings = (s) => { setSavings(s); };
  const savePrices = (p) => { setPriceCache(p); ls.set('ptr_prices', p); };
  // Functional update — safe inside concurrent fetchPrice loops where the closure-captured
  // priceCache would otherwise overwrite earlier writes.
  const mergePriceEntry = (sym, entry) => {
    setPriceCache(prev => {
      const next = { ...prev, [sym]: entry };
      ls.set('ptr_prices', next);
      return next;
    });
  };
  const saveRE = (r) => { setRealEstate(r); };
  const saveOA = (o) => { setOtherAssets(o); };
  const saveLogoCache = (c) => { setLogoCache(c); ls.set('ptr_logo_cache', c); };

  // ── Hydrate from backend on mount ──
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/store/patrimoine`, hAuth);
        if (cancelled) return;
        const d = r.data || {};
        setAccounts(d.accounts || []);
        setPositions(d.positions || []);
        setSavings(d.savings || []);
        setRealEstate(d.realestate || []);
        setOtherAssets(d.otherassets || []);
        setSavLinks(d.savLinks || {});
      } catch (e) {
        console.warn('Failed to load /store/patrimoine', e);
      } finally {
        if (!cancelled) { hydratedRef.current = true; setStoreLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Autosave to backend (debounced 500ms) on state changes ──
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      axios.put(`${API}/store/patrimoine`, {
        accounts, positions, savings, realestate: realEstate, otherassets: otherAssets, savLinks,
      }, hAuth).catch(e => console.warn('Autosave patrimoine failed', e));
    }, 500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [accounts, positions, savings, realEstate, otherAssets, savLinks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch crypto logos via CoinGecko ──
  const cryptoFetchDone = React.useRef(false);
  useEffect(() => {
    if (cryptoFetchDone.current) return;
    const cryptoPositions = positions.filter(p => p.type === 'Crypto');
    const missing = cryptoPositions.filter(p => {
      const key = p.ticker?.split('.')[0]?.split(':').pop()?.toUpperCase();
      return key && CRYPTO_MAP[key] && (!logoCache[`crypto_${key}`] || Date.now() - logoCache[`crypto_${key}`].ts > 604800000);
    });
    if (missing.length === 0) return;
    cryptoFetchDone.current = true;
    (async () => {
      const newCache = { ...logoCache };
      for (const p of missing) {
        const key = p.ticker?.split('.')[0]?.split(':').pop()?.toUpperCase();
        const cgId = CRYPTO_MAP[key];
        try {
          const res = await axios.get(`https://api.coingecko.com/api/v3/coins/${cgId}?localization=false&tickers=false&community_data=false&developer_data=false`);
          if (res.data?.image?.small) newCache[`crypto_${key}`] = { url: res.data.image.small, ts: Date.now() };
        } catch {}
        await new Promise(r => setTimeout(r, 400));
      }
      saveLogoCache(newCache);
    })();
  }, [positions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load debts/objectives from backend ──
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/debts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setDebts(r.data)).catch(() => {});
    axios.get(`${API}/objectives`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setObjectives(r.data)).catch(() => {});
  }, [token]);

  // ── FX rate fetch (Yahoo Finance EUR{XXX}=X) — used to convert non-EUR Yahoo quotes to EUR ──
  // Yahoo's EURUSD=X returns "1 EUR = X USD". To convert USD → EUR we divide the USD price by X.
  const getFxRate = useCallback(async (currency) => {
    if (!currency || currency === 'EUR') return 1.0;
    const cur = String(currency).toUpperCase();
    const cached = fxCache[cur];
    if (cached && Date.now() - cached.ts < 3600000) return cached.rate;
    try {
      const fxSymbol = `EUR${cur}=X`;
      const res = await axios.get(`${API}/market/quote/${encodeURIComponent(fxSymbol)}`);
      const rate = res.data?.price;
      if (rate && rate > 0) {
        setFxCache(prev => {
          const next = { ...prev, [cur]: { rate, ts: Date.now() } };
          ls.set('ptr_fx_cache', next);
          return next;
        });
        return rate;
      }
    } catch (e) {
      console.warn(`Failed to fetch FX rate for ${cur}`, e);
    }
    return cached?.rate || null; // null = unknown rate, caller must handle
  }, [fxCache, API, ls]);

  // ── Price fetch (Yahoo Finance via backend proxy, CoinGecko for crypto) ──
  const fetchPrice = useCallback(async (symbol, posType) => {
    if (!symbol) return null;
    const cached = priceCache[symbol];
    if (cached && Date.now() - cached.ts < 3600000) return cached.price;
    try {
      let price, change, currency = 'EUR';
      if (posType === 'Crypto') {
        // CoinGecko for crypto — vs_currency=eur already returns EUR-denominated prices.
        const base = symbol.split('.')[0].split(':').pop().toUpperCase();
        const cgId = CRYPTO_MAP[base];
        if (cgId) {
          const res = await axios.get(`https://api.coingecko.com/api/v3/simple/price?ids=${cgId}&vs_currencies=eur&include_24hr_change=true`);
          price = res.data[cgId]?.eur;
          change = res.data[cgId]?.eur_24h_change || 0;
        } else {
          // Search CoinGecko for unknown crypto
          const search = await axios.get(`https://api.coingecko.com/api/v3/search?query=${base}`);
          const coin = search.data?.coins?.[0];
          if (coin) {
            const res = await axios.get(`https://api.coingecko.com/api/v3/simple/price?ids=${coin.id}&vs_currencies=eur&include_24hr_change=true`);
            price = res.data[coin.id]?.eur;
            change = res.data[coin.id]?.eur_24h_change || 0;
          }
        }
      } else {
        // Yahoo Finance via backend proxy
        const res = await axios.get(`${API}/market/quote/${encodeURIComponent(symbol)}`);
        price = res.data.price;
        change = res.data.change_percent || 0;
        currency = (res.data.currency || 'EUR').toUpperCase();
        const nativePrice = price;
        let fxRate = null;
        // ─── Convert to EUR when Yahoo quotes the asset in another currency (e.g. USD for AT&T 'T').
        //     We divide the price by EURXXX=X (which is "1 EUR = X foreign units").
        if (currency !== 'EUR' && price) {
          fxRate = await getFxRate(currency);
          if (fxRate && fxRate > 0) {
            price = price / fxRate;
          } else {
            // FX lookup failed — flag the cache entry so caller can decide what to do.
            currency = `${currency}_UNCONVERTED`;
          }
        }
        if (price) {
          mergePriceEntry(symbol, { price, change, currency, nativePrice, fxRate, ts: Date.now() });
          return price;
        }
      }
      if (price) {
        mergePriceEntry(symbol, { price, change, currency, ts: Date.now() });
        return price;
      }
    } catch {}
    return cached?.price || null;
  }, [priceCache, getFxRate]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAllPrices = async () => {
    setLoadingPrices(true);
    for (const p of positions) {
      if (!p.ticker) continue;
      await fetchPrice(p.ticker, p.type);
      await new Promise(r => setTimeout(r, 400));
    }
    setLoadingPrices(false);
    toast.success('Prix actualisés');
  };

  // Auto-fetch on mount
  const autoFetchDone = React.useRef(false);
  useEffect(() => {
    if (autoFetchDone.current) return;
    const withTicker = positions.filter(p => p.ticker);
    if (withTicker.length === 0) return;
    const stale = withTicker.some(p => !priceCache[p.ticker] || Date.now() - priceCache[p.ticker].ts > 3600000);
    if (!stale) return;
    autoFetchDone.current = true;
    (async () => {
      setLoadingPrices(true);
      for (const p of withTicker) {
        await fetchPrice(p.ticker, p.type);
        await new Promise(r => setTimeout(r, 400));
      }
      setLoadingPrices(false);
    })();
  }, [positions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Historical data for modal chart (Yahoo Finance via backend) ──
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histPeriod, setHistPeriod] = useState('1A');

  const fetchHistory = useCallback(async (symbol, posType) => {
    if (!symbol) return;
    const cacheKey = `ptr_hist_${symbol}`;
    const cached = ls.get(cacheKey, null);
    if (cached && Date.now() - cached.ts < 86400000) { setHistoryData(cached.data); return; }
    setHistoryLoading(true);
    try {
      let data = [];
      if (posType === 'Crypto') {
        // CoinGecko historical for crypto
        const base = symbol.split('.')[0].split(':').pop().toUpperCase();
        const cgId = CRYPTO_MAP[base] || base.toLowerCase();
        const res = await axios.get(`https://api.coingecko.com/api/v3/coins/${cgId}/market_chart?vs_currency=eur&days=1825`);
        if (res.data?.prices) {
          data = res.data.prices.map(([ts, price]) => ({
            date: new Date(ts).toISOString().slice(0, 10),
            close: price
          }));
          // Deduplicate by date (keep last entry per day)
          const byDate = {};
          data.forEach(d => { byDate[d.date] = d.close; });
          data = Object.entries(byDate).sort().map(([date, close]) => ({ date, close }));
        }
      } else {
        // Yahoo Finance via backend proxy — fetch max range
        const res = await axios.get(`${API}/market/history/${encodeURIComponent(symbol)}?range=max&interval=1d`);
        data = res.data?.history || [];
      }
      if (data.length > 0) {
        setHistoryData(data);
        ls.set(cacheKey, { data, ts: Date.now() });
      }
    } catch {}
    setHistoryLoading(false);
  }, [ls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter history by period
  const filteredHistory = useMemo(() => {
    if (!historyData) return [];
    const now = new Date();
    let cutoff;
    if (histPeriod === 'YTD') cutoff = new Date(now.getFullYear(), 0, 1);
    else if (histPeriod === '1A') cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    else if (histPeriod === '3A') cutoff = new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
    else if (histPeriod === '5A') cutoff = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
    else return historyData; // MAX
    return historyData.filter(d => new Date(d.date) >= cutoff);
  }, [historyData, histPeriod]);

  // ── KPI computations ──
  const kpis = useMemo(() => {
    let totalInvest = 0, totalSavings = 0, totalDebt = 0, totalRE = 0, totalOA = 0;
    positions.forEach(p => { totalInvest += (p.quantity || 0) * (priceCache[p.ticker]?.price || p.price); });
    savings.forEach(s => { totalSavings += parseFloat(s.balance) || 0; });
    debts.forEach(d => {
      const amort = computeAmortization(d.total_amount, d.monthly_payment, d.interest_rate || 0, d.start_date, d.prepayments || []);
      const pct = parseFloat(d.ownership_pct);
      const sharePct = Number.isFinite(pct) && pct > 0 ? pct : 100;
      totalDebt += amort.capitalRestant * (sharePct / 100);
    });
    realEstate.forEach(r => { totalRE += parseFloat(r.value) || 0; });
    otherAssets.forEach(a => { totalOA += parseFloat(a.value) || 0; });
    const brut = totalInvest + totalSavings + totalRE + totalOA;
    return { brut, invest: totalInvest, savings: totalSavings, debt: totalDebt, re: totalRE, oa: totalOA, net: brut - totalDebt };
  }, [positions, savings, debts, realEstate, otherAssets, priceCache]);

  // ── Projection data ──
  const projectionData = useMemo(() => {
    const periodMap = { 'YTD': 1, '1A': 1, '5A': 5, '10A': 10, 'Tout': 20 };
    const years = periodMap[overviewPeriod] || 5;
    const months = years * 12;
    const r = (parseFloat(projReturn) || 7) / 100 / 12;
    const m = parseFloat(projMonthly) || 0;
    const data = [];
    let cum = kpis.net;
    for (let i = 0; i <= months; i += (months > 60 ? 3 : 1)) {
      data.push({ month: i, label: i === 0 ? 'Auj.' : `+${Math.floor(i / 12)}a${i % 12 > 0 ? `${i % 12}m` : ''}`, real: i === 0 ? cum : null, proj: cum });
      cum = cum * (1 + r) + m * (months > 60 ? 3 : 1);
    }
    return data;
  }, [kpis.net, projMonthly, projReturn, overviewPeriod]);

  // ── Patrimoine composition donut ──
  const patrimoineDonut = useMemo(() => {
    const data = [];
    if (kpis.invest > 0) data.push({ name: 'Investissements', value: Math.round(kpis.invest) });
    if (kpis.savings > 0) data.push({ name: 'Épargne', value: Math.round(kpis.savings) });
    if (kpis.re > 0) data.push({ name: 'Immobilier', value: Math.round(kpis.re) });
    if (kpis.oa > 0) data.push({ name: 'Autres actifs', value: Math.round(kpis.oa) });
    return data;
  }, [kpis]);

  // ── Detail donuts ──
  const donuts = useMemo(() => {
    const byType = {}, byAccount = {}, byGeo = {}, bySector = {};
    positions.forEach(p => {
      const val = (p.quantity || 0) * (priceCache[p.ticker]?.price || p.price || 0);
      byType[p.type] = (byType[p.type] || 0) + val;
      const acctName = accounts.find(a => a.id === p.account)?.name || 'Autre';
      byAccount[acctName] = (byAccount[acctName] || 0) + val;
      byGeo[p.geo || 'Autre'] = (byGeo[p.geo || 'Autre'] || 0) + val;
      bySector[p.sector || 'Autre'] = (bySector[p.sector || 'Autre'] || 0) + val;
    });
    savings.forEach(s => {
      byType['Épargne'] = (byType['Épargne'] || 0) + (parseFloat(s.balance) || 0);
      byAccount[s.name] = (byAccount[s.name] || 0) + (parseFloat(s.balance) || 0);
    });
    realEstate.forEach(r => {
      byType['Immobilier'] = (byType['Immobilier'] || 0) + (parseFloat(r.value) || 0);
      byAccount[r.name] = (byAccount[r.name] || 0) + (parseFloat(r.value) || 0);
    });
    otherAssets.forEach(a => {
      byType[a.category || 'Autre'] = (byType[a.category || 'Autre'] || 0) + (parseFloat(a.value) || 0);
      byAccount[a.name] = (byAccount[a.name] || 0) + (parseFloat(a.value) || 0);
    });
    const toArr = (obj) => Object.entries(obj).map(([name, value]) => ({ name, value: Math.round(value) })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
    return { byType: toArr(byType), byAccount: toArr(byAccount), byGeo: toArr(byGeo), bySector: toArr(bySector) };
  }, [positions, savings, realEstate, otherAssets, accounts, priceCache]);

  // ── Position enrichment ──
  const enrichedPositions = useMemo(() => {
    return positions.map(p => {
      const cached = priceCache[p.ticker];
      const currentPrice = cached?.price || p.price;
      const change = cached?.change || 0;
      const isStale = cached && Date.now() - cached.ts > 3600000;
      const cost = (p.quantity || 0) * (p.price || 0);
      const value = (p.quantity || 0) * currentPrice;
      const perfEuro = value - cost;
      const perfPct = cost > 0 ? ((currentPrice - p.price) / p.price) * 100 : 0;
      const acct = accounts.find(a => a.id === p.account);
      return {
        ...p, currentPrice, change, isStale, cost, value, perfEuro, perfPct,
        priceCurrency: cached?.currency || 'EUR',
        nativePrice: cached?.nativePrice ?? null,
        fxRate: cached?.fxRate ?? null,
        accountName: acct?.name || '—', transactions: p.transactions || []
      };
    });
  }, [positions, priceCache, accounts]);

  // ── CRUD: Accounts & Positions ──
  const addAccount = () => {
    if (!acctForm.name) return;
    saveAccounts([...accounts, { ...acctForm, id: Date.now().toString(36) }]);
    setAcctForm(emptyAcct); setAcctDlg(false);
    toast.success(`Compte "${acctForm.name}" créé`);
  };

  const addPosition = () => {
    if (!posForm.name || !posForm.quantity || !posForm.price) return;
    const qty = parseFloat(posForm.quantity);
    const prx = parseFloat(posForm.price);
    // Detect existing position by ticker (case-insensitive) + account; fallback to name+account
    const tickerKey = (posForm.ticker || '').trim().toUpperCase();
    const existing = positions.find(p => {
      const sameAccount = (p.account || '') === (posForm.account || '');
      if (!sameAccount) return false;
      if (tickerKey) return ((p.ticker || '').trim().toUpperCase() === tickerKey);
      return p.name?.trim().toLowerCase() === posForm.name.trim().toLowerCase();
    });
    if (existing) {
      const tx = { id: Date.now().toString(36), type: 'buy', quantity: qty, price: prx, date: posForm.date, amount: qty * prx };
      const updated = positions.map(p => {
        if (p.id !== existing.id) return p;
        const txs = [...(p.transactions || []), tx];
        let totalQty = 0, totalCost = 0;
        txs.forEach(t => { if (t.type === 'buy') { totalQty += t.quantity; totalCost += t.quantity * t.price; } else { totalQty -= t.quantity; } });
        return { ...p, transactions: txs, quantity: totalQty, price: totalCost / (totalQty || 1) };
      });
      savePositions(updated);
      setPosForm(emptyPos); setPosDlg(false);
      toast.success(`Transaction ajoutée à votre position existante ${existing.name}`);
      if (existing.ticker) fetchPrice(existing.ticker, existing.type);
      return;
    }
    const p = {
      ...posForm, id: Date.now().toString(36),
      quantity: qty, price: prx,
      transactions: [{ id: 'tx0', type: 'buy', quantity: qty, price: prx, date: posForm.date, amount: qty * prx }]
    };
    savePositions([...positions, p]);
    setPosForm(emptyPos); setPosDlg(false);
    toast.success(`Position "${p.name}" ajoutée`);
    if (p.ticker) fetchPrice(p.ticker, p.type);
  };

  const addTransaction = () => {
    if (!selectedPos || !txForm.quantity || !txForm.price) return;
    const tx = { id: Date.now().toString(36), ...txForm, quantity: parseFloat(txForm.quantity), price: parseFloat(txForm.price), amount: parseFloat(txForm.quantity) * parseFloat(txForm.price) };
    const updated = positions.map(p => {
      if (p.id !== selectedPos.id) return p;
      const txs = [...(p.transactions || []), tx];
      let totalQty = 0, totalCost = 0;
      txs.forEach(t => { if (t.type === 'buy') { totalQty += t.quantity; totalCost += t.quantity * t.price; } else { totalQty -= t.quantity; } });
      return { ...p, transactions: txs, quantity: totalQty, price: totalCost / (totalQty || 1) };
    });
    savePositions(updated);
    setSelectedPos(updated.find(p => p.id === selectedPos.id));
    setTxForm(emptyTx); setTxDlg(false);
    toast.success('Transaction ajoutée');
  };

  const deletePosition = (id) => {
    if (!window.confirm('Supprimer cette position ?')) return;
    savePositions(positions.filter(p => p.id !== id));
    setPosDetailOpen(false);
    toast.success('Position supprimée');
  };

  const deleteAccount = (acctId) => {
    setDeleteAcctConfirm(acctId);
  };
  const confirmDeleteAccount = () => {
    if (!deleteAcctConfirm) return;
    savePositions(positions.filter(p => p.account !== deleteAcctConfirm));
    saveAccounts(accounts.filter(a => a.id !== deleteAcctConfirm));
    setAccountFilter('all');
    setDeleteAcctConfirm(null);
    toast.success('Compte supprimé');
  };

  const deleteTransaction = (posId, txId) => {
    if (!window.confirm('Supprimer cette transaction ? Le PRU sera recalculé.')) return;
    const updated = positions.map(p => {
      if (p.id !== posId) return p;
      const txs = (p.transactions || []).filter(t => t.id !== txId);
      let totalQty = 0, totalCost = 0;
      txs.forEach(t => { if (t.type === 'buy') { totalQty += t.quantity; totalCost += t.quantity * t.price; } else { totalQty -= t.quantity; } });
      return { ...p, transactions: txs, quantity: totalQty, price: totalQty > 0 ? totalCost / totalQty : 0 };
    });
    savePositions(updated);
    const updatedPos = updated.find(p => p.id === posId);
    if (updatedPos) setSelectedPos(updatedPos);
    toast.success('Transaction supprimée');
  };

  const openEditPosition = (p, e) => {
    if (e) e.stopPropagation();
    setEditPosForm({ id: p.id, name: p.name, ticker: p.ticker || '', isin: p.isin || '', type: p.type, account: p.account || '', geo: p.geo || 'Europe', sector: p.sector || 'Tech' });
    setEditPosDlg(true);
  };

  const saveEditPosition = () => {
    if (!editPosForm?.name) return;
    const updated = positions.map(p => p.id === editPosForm.id ? { ...p, name: editPosForm.name, ticker: editPosForm.ticker, isin: editPosForm.isin, type: editPosForm.type, account: editPosForm.account, geo: editPosForm.geo, sector: editPosForm.sector } : p);
    savePositions(updated);
    setEditPosDlg(false);
    toast.success('Position modifiée');
  };

  // ── CRUD: Savings ──
  const addSaving = () => {
    if (!savForm.balance) return;
    const finalType = savForm.accountType === 'Autre' ? (savForm.customType || 'Autre') : savForm.accountType;
    if (!finalType) return;
    const finalName = savForm.name || finalType;
    const payload = {
      id: Date.now().toString(36),
      category: savForm.category,
      accountType: finalType,
      name: finalName,
      institution: savForm.institution,
      rate: parseFloat(savForm.rate) || 0,
      balance: parseFloat(savForm.balance) || 0,
      date: savForm.date,
      // Employer match (only meaningful for "Épargne salariale"). Each new deposit credited
      // through FinancesPage adds `versement * pct/100` to balance + cumulative tracker.
      employerMatchPct: savForm.category === 'salariale' ? (parseFloat(savForm.employerMatchPct) || 0) : 0,
      employerMatchTotal: 0,
    };
    saveSavings([...savings, payload]);
    setSavForm(emptySav); setSavDlg(false);
    toast.success(`Compte "${finalName}" ajouté`);
  };
  const deleteSaving = (id) => { saveSavings(savings.filter(s => s.id !== id)); toast.success('Compte supprimé'); };
  const updateSavBalance = (id, newBal) => { saveSavings(savings.map(s => s.id === id ? { ...s, balance: parseFloat(newBal) || 0 } : s)); };

  // ── Allocations: open modal, mutate items, save ──
  const openAllocDlg = (saving) => {
    const items = (saving.allocations || []).map(a => ({ ...a }));
    setAllocDlg({ open: true, savingId: saving.id, items });
  };
  const updateAllocItem = (idx, patch) => {
    setAllocDlg(prev => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };
  const addAllocItem = () => {
    setAllocDlg(prev => ({ ...prev, items: [...prev.items, { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 5), name: '', amount: '', objectiveId: null }] }));
  };
  const removeAllocItem = (idx) => {
    setAllocDlg(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };
  const saveAllocations = () => {
    const acct = savings.find(s => s.id === allocDlg.savingId);
    if (!acct) return;
    const balance = parseFloat(acct.balance) || 0;
    const cleaned = allocDlg.items
      .map(a => ({ id: a.id, name: (a.name || '').trim(), amount: parseFloat(a.amount) || 0, objectiveId: a.objectiveId || null }))
      .filter(a => a.name && a.amount > 0);
    const total = cleaned.reduce((s, a) => s + a.amount, 0);
    if (total > balance + 0.005) {
      toast.error(`Allocations (${fmt(total)}) > solde du compte (${fmt(balance)})`);
      return;
    }
    saveSavings(savings.map(s => s.id === allocDlg.savingId ? { ...s, allocations: cleaned } : s));
    setAllocDlg({ open: false, savingId: null, items: [] });
    toast.success('Allocations enregistrées');
  };

  // ── CRUD: Debts (backend) ──

  const syncDebtToBudget = async (debt, action = 'add') => {
    // Read finances blob from backend
    let financesBlob;
    try {
      const r = await axios.get(`${API}/store/finances`, hAuth);
      financesBlob = r.data || {};
    } catch { financesBlob = {}; }
    const budgetData = financesBlob.budget || { revenues: [], fixedCharges: [], variableCharges: [], envelopes: [] };
    const cat = DEBT_TYPE_MAP[debt.type || debt.debt_type] || 'Crédit';
    // Effective monthly = full monthly × quote-part. Defaults to 100% when not set.
    const pct = parseFloat(debt.ownership_pct);
    const sharePct = Number.isFinite(pct) && pct > 0 ? pct : 100;
    const effectiveMonthly = (parseFloat(debt.monthly_payment) || 0) * (sharePct / 100);
    // Compute the loan's last-payment date (start_date + totalMonths months) so the linked
    // recurring expense automatically disappears from future months once the loan is paid off.
    // Recomputed every time the debt changes (creation, prepayment, etc.) so the end date
    // shifts forward/backward with the schedule.
    let endDate = null;
    if (debt.start_date && debt.total_amount && debt.monthly_payment) {
      try {
        const amort = computeAmortization(
          debt.total_amount,
          debt.monthly_payment,
          debt.interest_rate || 0,
          debt.start_date,
          debt.prepayments || []
        );
        if (amort.totalMonths > 0 && amort.totalMonths < 9999) {
          const start = new Date(debt.start_date);
          if (!Number.isNaN(start.getTime())) {
            // The last payment is at month index (totalMonths - 1).
            const end = new Date(start.getFullYear(), start.getMonth() + amort.totalMonths - 1, 28);
            const y = end.getFullYear();
            const m = String(end.getMonth() + 1).padStart(2, '0');
            endDate = `${y}-${m}-28`;
          }
        }
      } catch (e) { console.warn('Failed to compute debt end date', e); }
    }
    let fc = budgetData.fixedCharges || [];

    if (action === 'add') {
      const existingManual = fc.find(c => !c.debtId && Math.abs((parseFloat(c.amount) || 0) - effectiveMonthly) < 1 && c.name.toLowerCase().includes('crédit'));
      if (existingManual) {
        const merge = window.confirm(`Une charge fixe "${existingManual.name}" de ${existingManual.amount}€ existe déjà. Voulez-vous la fusionner avec cette dette ?\n\n• Oui = Remplacer par la charge liée\n• Non = Créer une nouvelle charge`);
        if (merge) fc = fc.filter(c => c !== existingManual);
      }
      fc.push({ name: debt.name, amount: effectiveMonthly, category: cat, debtId: debt.id, isDebtLinked: true, endDate });
    } else if (action === 'remove') {
      fc = fc.filter(c => c.debtId !== debt.id);
    } else if (action === 'update') {
      fc = fc.map(c => c.debtId === debt.id ? { ...c, name: debt.name, amount: effectiveMonthly, category: cat, endDate } : c);
    }

    budgetData.fixedCharges = fc;
    financesBlob.budget = budgetData;
    try {
      await axios.put(`${API}/store/finances`, financesBlob, hAuth);
    } catch (e) { console.warn('Failed to sync debt to budget', e); }
  };

  const addDebt = async () => {
    if (!debtForm.name || !debtForm.total_amount) return;
    try {
      const pct = parseFloat(debtForm.ownership_pct);
      const payload = {
        name: debtForm.name,
        debt_type: debtForm.type || 'immobilier',
        total_amount: parseFloat(debtForm.total_amount),
        remaining_amount: parseFloat(debtForm.total_amount),
        monthly_payment: parseFloat(debtForm.monthly_payment) || 0,
        interest_rate: parseFloat(debtForm.interest_rate) || 0,
        start_date: debtForm.start_date || today,
        ownership_pct: Number.isFinite(pct) && pct > 0 ? pct : 100,
        prepayments: [],
      };
      const r = await axios.post(`${API}/debts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setDebts([...debts, r.data]);
      syncDebtToBudget({ ...r.data, type: payload.debt_type }, 'add');
      setDebtForm(emptyDebt); setDebtDlg(false);
      toast.success('Dette ajoutée — la mensualité a été ajoutée à tes charges fixes');
    } catch { toast.error('Erreur'); }
  };

  // ── Early repayment: append a prepayment entry on the debt (backend PUT).
  // The amortization schedule is recomputed on the fly via computeAmortization(...prepayments).
  const addPrepayment = async (debt, amount, date) => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Montant invalide'); return false; }
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) { toast.error('Date invalide'); return false; }
    const existing = Array.isArray(debt.prepayments) ? debt.prepayments : [];
    const next = [...existing, { amount: amt, date }];
    const payload = {
      name: debt.name,
      debt_type: debt.debt_type || debt.type || 'immobilier',
      total_amount: parseFloat(debt.total_amount),
      remaining_amount: parseFloat(debt.remaining_amount ?? debt.total_amount),
      monthly_payment: parseFloat(debt.monthly_payment) || 0,
      interest_rate: parseFloat(debt.interest_rate) || 0,
      start_date: debt.start_date,
      ownership_pct: parseFloat(debt.ownership_pct) || 100,
      prepayments: next,
    };
    try {
      await axios.put(`${API}/debts/${debt.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setDebts(prev => prev.map(d => d.id === debt.id ? { ...d, prepayments: next } : d));
      // Re-sync the linked recurring expense so its end_date reflects the new (shorter) schedule.
      syncDebtToBudget({ ...debt, prepayments: next, type: debt.debt_type }, 'update');
      toast.success(`Remboursement anticipé de ${fmt(amt)} enregistré`);
      return true;
    } catch { toast.error('Échec de la mise à jour'); return false; }
  };

  const deleteDebt = async (id) => {
    const debt = debts.find(d => d.id === id);
    try {
      await axios.delete(`${API}/debts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDebts(debts.filter(d => d.id !== id));
      if (debt) syncDebtToBudget(debt, 'remove');
      toast.success('Supprimée — la charge fixe associée a été retirée');
    } catch { toast.error('Erreur'); }
  };

  // ── CRUD: Real Estate ──
  const addRealEstate = () => {
    if (!reForm.name || !reForm.value) return;
    saveRE([...realEstate, { ...reForm, id: Date.now().toString(36), value: parseFloat(reForm.value), purchase_price: parseFloat(reForm.purchase_price) || 0 }]);
    setReForm(emptyRE); setReDlg(false);
    toast.success(`Bien "${reForm.name}" ajouté`);
  };
  const deleteRE = (id) => { saveRE(realEstate.filter(r => r.id !== id)); toast.success('Bien supprimé'); };

  // ── CRUD: Other Assets ──
  const addOtherAsset = () => {
    if (!oaForm.name || !oaForm.value) return;
    saveOA([...otherAssets, { ...oaForm, id: Date.now().toString(36), value: parseFloat(oaForm.value), purchase_price: parseFloat(oaForm.purchase_price) || 0 }]);
    setOaForm(emptyOA); setOaDlg(false);
    toast.success(`Actif "${oaForm.name}" ajouté`);
  };
  const deleteOA = (id) => { saveOA(otherAssets.filter(a => a.id !== id)); toast.success('Actif supprimé'); };

  // Savings → Objectives link
  const linkSav = (savId, goalId) => { setSavLinks(l => ({ ...l, [savId]: goalId })); toast.success('Lié !'); };
  const unlinkSav = (savId) => { setSavLinks(l => { const c = { ...l }; delete c[savId]; return c; }); };

  // Sync envelope cumulative amounts to /api/store/finances.goal_overrides for ObjectivesPage
  useEffect(() => {
    if (!token || !Object.keys(savLinks).length) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/store/finances`, hAuth);
        const finances = r.data || {};
        const overrides = { ...(finances.goal_overrides || {}) };
        let changed = false;
        Object.entries(savLinks).forEach(([savId, goalId]) => {
          const s = savings.find(sv => sv.id === savId);
          if (!s) return;
          // When the account has explicit allocations, those become the source of truth for
          // objective progression. Skip the direct savLinks override here AND wipe any prior
          // override on this goal so the value isn't double-counted on top of allocation_total.
          const hasAllocations = Array.isArray(s.allocations) && s.allocations.length > 0;
          if (hasAllocations) {
            if (overrides[goalId]) { delete overrides[goalId]; changed = true; }
            return;
          }
          if ((overrides[goalId] || 0) !== (parseFloat(s.balance) || 0)) {
            overrides[goalId] = parseFloat(s.balance) || 0;
            changed = true;
          }
        });
        if (changed) {
          finances.goal_overrides = overrides;
          await axios.put(`${API}/store/finances`, finances, hAuth);
        }
      } catch (e) { console.warn('Failed to sync goal_overrides', e); }
    }, 600);
    return () => clearTimeout(t);
  }, [savings, savLinks, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Projection UI prefs: synced with backend (db.user_data.prefs) ──
  useEffect(() => {
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/user-prefs/ptr_proj_monthly`, { headers: h })
      .then(r => { if (r.data?.value != null) setProjMonthly(r.data.value); })
      .catch(() => {});
    axios.get(`${API}/user-prefs/ptr_proj_return`, { headers: h })
      .then(r => { if (r.data?.value != null) setProjReturn(r.data.value); })
      .catch(() => {});
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => {
      axios.put(`${API}/user-prefs/ptr_proj_monthly`, { value: projMonthly },
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [projMonthly, token]); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => {
      axios.put(`${API}/user-prefs/ptr_proj_return`, { value: projReturn },
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [projReturn, token]); // eslint-disable-line

  // Fetch history when detail modal opens
  useEffect(() => {
    if (posDetailOpen && selectedPos?.ticker) { fetchHistory(selectedPos.ticker, selectedPos.type); }
    else if (!posDetailOpen) { setHistoryData(null); setHistPeriod('1A'); }
  }, [posDetailOpen, selectedPos?.ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Donut component ──
  const DonutChart = ({ data, title, large }) => (
    <div className={large ? '' : 'glass-card p-5'}>
      {title && <h4 className="text-white text-sm font-semibold mb-3">{title}</h4>}
      {data.length > 0 ? (
        <div className={large ? 'flex flex-col items-center' : 'flex items-center gap-4'}>
          <div className={large ? 'w-44 h-44' : 'w-28 h-28'}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={large ? 50 : 30} outerRadius={large ? 70 : 50} paddingAngle={2}>{data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }} formatter={v => fmt(v)} /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className={`${large ? 'w-full mt-3' : 'flex-1'} space-y-1.5`}>
            {data.slice(0, 6).map((d, i) => {
              const total = data.reduce((s, x) => s + x.value, 0);
              return (<div key={d.name} className="flex items-center justify-between text-xs"><span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="truncate">{d.name}</span></span><span className="text-cap-text-muted ml-2 whitespace-nowrap">{fmt(d.value)} ({total > 0 ? ((d.value / total) * 100).toFixed(0) : 0}%)</span></div>);
            })}
          </div>
        </div>
      ) : <p className="text-cap-text-muted text-xs text-center py-6">Pas de données</p>}
    </div>
  );

  // ── Logo helper (Apistemic for actions/brokers, CoinGecko for crypto) ──
  // Image overlays fallback initials. onerror hides image directly on DOM — no React state needed.
  const sizeMap = { 6: 24, 8: 32, 10: 40, 12: 48 };
  const Logo = ({ name, ticker, type, broker, size = 8 }) => {
    const base = ticker?.split('.')[0]?.split(':').pop()?.toUpperCase();
    const initials = (name || broker || '??').slice(0, 2).toUpperCase();
    const color = COLORS[(name || broker || '').charCodeAt(0) % COLORS.length || 0];
    const px = sizeMap[size] || 32;

    let imgSrc = null;
    if (type === 'Crypto' && base) {
      imgSrc = logoCache[`crypto_${base}`]?.url || null;
    } else if (broker && !ticker) {
      const d = BROKER_MAP[broker] || BROKER_MAP[broker.toLowerCase?.()];
      if (d) imgSrc = `https://logos-api.apistemic.com/domain:${d}`;
    } else {
      const d = TICKER_DOMAIN_MAP[base] || (ticker && !ticker.includes('.') ? `${ticker.toLowerCase()}.com` : null);
      if (d) imgSrc = `https://logos-api.apistemic.com/domain:${d}`;
    }

    return (
      <div className="relative shrink-0 rounded-lg overflow-hidden" style={{ width: px, height: px }}>
        <div className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ backgroundColor: `${color}20`, color }}>{initials}</div>
        {imgSrc && <img src={imgSrc} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
      </div>
    );
  };

  // ── Institution Picker (dropdown with search + logos) ──
  const InstitutionPicker = ({ value, onChange, placeholder, institutions: customList }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [customMode, setCustomMode] = useState(false);
    const [customValue, setCustomValue] = useState('');

    const list = customList || INSTITUTIONS;
    const q = search.toLowerCase();
    const filteredInst = q ? list.filter(i => i.name.toLowerCase().includes(q) || i.cat.toLowerCase().includes(q)) : list;
    const cats = [...new Set(list.map(i => i.cat))];
    const grouped = {};
    cats.forEach(c => { grouped[c] = []; });
    filteredInst.forEach(i => { if (grouped[i.cat]) grouped[i.cat].push(i); });

    const selected = list.find(i => i.name === value);

    const InstitutionLogo = ({ domain, name, size = 8 }) => {
      const initials = (name || '??').slice(0, 2).toUpperCase();
      const color = COLORS[(name || '').charCodeAt(0) % COLORS.length || 0];
      const px = sizeMap[size] || 32;
      return (
        <div className="relative shrink-0 rounded-md overflow-hidden" style={{ width: px, height: px }}>
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: `${color}20`, color }}>{initials}</div>
          {domain && <img src={`https://logos-api.apistemic.com/domain:${domain}`} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
        </div>
      );
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-2.5 w-full h-10 px-3 rounded-md border border-white/10 bg-cap-bg text-sm text-left hover:bg-white/[0.03] transition-colors" data-testid="institution-picker">
            {selected ? (
              <><InstitutionLogo domain={selected.domain} name={selected.name} /><span className="text-white truncate">{selected.name}</span></>
            ) : value ? (
              <><InstitutionLogo domain={null} name={value} /><span className="text-white truncate">{value}</span></>
            ) : (
              <span className="text-cap-text-muted">{placeholder || 'Choisir un établissement'}</span>
            )}
            <ChevronDown className="w-4 h-4 text-cap-text-muted ml-auto shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 bg-cap-bg-elevated border-white/10" align="start" sideOffset={4}>
          {customMode ? (
            <div className="p-3 space-y-3">
              <p className="text-cap-text-muted text-xs">Saisir un nom manuellement :</p>
              <Input value={customValue} onChange={e => setCustomValue(e.target.value)} placeholder="Nom de l'établissement" className="bg-cap-bg border-white/10 text-white text-sm" autoFocus />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-cap-emerald text-cap-bg text-xs" onClick={() => { if (customValue.trim()) { onChange(customValue.trim()); setOpen(false); setCustomMode(false); setCustomValue(''); } }}>Valider</Button>
                <Button size="sm" variant="outline" className="border-white/10 text-cap-text-muted text-xs" onClick={() => setCustomMode(false)}>Retour</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="p-2 border-b border-white/5">
                <div className="flex items-center gap-2 px-2 py-1 rounded bg-cap-bg">
                  <Search className="w-3.5 h-3.5 text-cap-text-muted shrink-0" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="bg-transparent text-white text-sm outline-none w-full placeholder:text-cap-text-muted" autoFocus />
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto p-1" style={{ scrollbarColor: '#00e5a0 #1a1d24', scrollbarWidth: 'thin' }}>
                {Object.entries(grouped).map(([cat, items]) => items.length > 0 && (
                  <div key={cat}>
                    <p className="text-cap-text-muted text-[10px] uppercase tracking-wider px-2 pt-2 pb-1">{cat}</p>
                    {items.map(inst => (
                      <button key={inst.name} type="button" onClick={() => { onChange(inst.name); setOpen(false); setSearch(''); }} className="flex items-center gap-2.5 w-full px-2 py-1.5 rounded hover:bg-white/5 transition-colors text-left" data-testid={`inst-${inst.domain}`}>
                        <InstitutionLogo domain={inst.domain} name={inst.name} size={6} />
                        <span className="text-white text-sm truncate">{inst.name}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {filteredInst.length === 0 && <p className="text-cap-text-muted text-xs text-center py-3">Aucun résultat</p>}
                <div className="border-t border-white/5 mt-1 pt-1">
                  <button type="button" onClick={() => { setCustomMode(true); setSearch(''); }} className="flex items-center gap-2.5 w-full px-2 py-1.5 rounded hover:bg-white/5 transition-colors text-left">
                    <div className="w-6 h-6 rounded-md bg-white/5 flex items-center justify-center"><Plus className="w-3.5 h-3.5 text-cap-text-muted" /></div>
                    <span className="text-cap-text-muted text-sm">Autre (saisie manuelle)</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  const filtered = accountFilter === 'all' ? enrichedPositions : enrichedPositions.filter(p => p.account === accountFilter);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="patrimoine-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white pt-8" data-testid="patrimoine-title">Patrimoine</h1>
          <p className="text-cap-text-muted text-sm mt-1">Vue globale de vos actifs, épargne et dettes</p>
        </div>
        <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs" onClick={() => setShowResetConfirm(true)} data-testid="reset-patrimoine-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Réinitialiser</Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="patrimoine-kpis">
        {[
          { label: 'Patrimoine brut', value: kpis.brut, color: '#00e5a0' },
          { label: 'Investissements', value: kpis.invest, color: '#4f8eff' },
          { label: 'Épargne', value: kpis.savings, color: '#00e5a0' },
          { label: 'Immobilier', value: kpis.re, color: '#7c3aed' },
          { label: 'Autres actifs', value: kpis.oa, color: '#f97316' },
          { label: 'Dettes', value: kpis.debt, color: '#ef4444' },
        ].map(k => (
          <div key={k.label} className="glass-card p-3">
            <p className="text-cap-text-muted text-xs mb-0.5">{k.label}</p>
            <p className="font-display text-base font-bold" style={{ color: k.color }}>{fmt(k.value)}</p>
          </div>
        ))}
      </div>
      {/* Patrimoine net — highlighted */}
      <div className="glass-card p-5 border-cap-emerald/30 border" data-testid="kpi-net">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-cap-text-muted text-xs">Patrimoine net</p>
            <p className="font-display text-3xl font-bold text-cap-emerald">{fmt(kpis.net)}</p>
          </div>
          <Wallet className="w-8 h-8 text-cap-emerald opacity-30" />
        </div>
      </div>

      {/* Tabs — 6 onglets */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-cap-bg-elevated p-1 rounded-xl border border-white/5 flex gap-1 overflow-x-auto mb-6">
          {[
            { v: 'overview', l: "Vue d'ensemble" }, { v: 'invest', l: 'Investissements' },
            { v: 'savings', l: 'Épargne' }, { v: 'realestate', l: 'Immobilier' },
            { v: 'otherassets', l: 'Autres actifs' }, { v: 'debts', l: 'Dettes' }
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="flex-1 rounded-lg data-[state=active]:bg-white/10 text-cap-text-muted data-[state=active]:text-white text-sm whitespace-nowrap px-3" data-testid={`tab-${t.v}`}>{t.l}</TabsTrigger>
          ))}
        </TabsList>

        {/* ═══ TAB 1: VUE D'ENSEMBLE (Bagrr-inspired 60/40) ═══ */}
        <TabsContent value="overview" data-testid="tab-content-overview">
          <div className="space-y-6">
            {/* Main 60/40 layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left col — 60% — Projection chart */}
              <div className="lg:col-span-3 glass-card p-6" data-testid="projection-chart">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3 className="text-white font-semibold">Évolution & Projection</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5"><Label className="text-cap-text-muted text-xs">Apport/mois</Label><Input type="number" value={projMonthly} onChange={e => setProjMonthly(e.target.value)} className="bg-cap-bg border-white/10 text-white text-sm w-20 h-7" /></div>
                    <div className="flex items-center gap-1.5"><Label className="text-cap-text-muted text-xs">Rend. %</Label><Input type="number" value={projReturn} onChange={e => setProjReturn(e.target.value)} className="bg-cap-bg border-white/10 text-white text-sm w-14 h-7" step="0.5" /></div>
                    <div className="flex gap-1">{['YTD', '1A', '5A', '10A', 'Tout'].map(p => <button key={p} onClick={() => setOverviewPeriod(p)} className={`px-2 py-0.5 rounded text-xs ${overviewPeriod === p ? 'bg-cap-emerald text-cap-bg font-semibold' : 'bg-cap-bg text-cap-text-muted'}`} data-testid={`period-${p}`}>{p}</button>)}</div>
                  </div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={projectionData}>
                      <defs><linearGradient id="projGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00e5a0" stopOpacity={0.2} /><stop offset="95%" stopColor="#00e5a0" stopOpacity={0} /></linearGradient></defs>
                      <XAxis dataKey="label" stroke="#64748b" fontSize={10} interval="preserveStartEnd" />
                      <YAxis stroke="#64748b" fontSize={10} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }} formatter={v => v !== null ? fmt(v) : ''} />
                      <Area type="monotone" dataKey="proj" stroke="#00e5a0" fill="url(#projGrad)" strokeWidth={2} strokeDasharray="6 3" name="Projection" />
                      <Line type="monotone" dataKey="real" stroke="#4f8eff" strokeWidth={3} dot={{ r: 5 }} name="Réel" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Right col — 40% — Donut composition */}
              <div className="lg:col-span-2 glass-card p-6" data-testid="patrimoine-donut">
                <h3 className="text-white font-semibold mb-4">Répartition du patrimoine</h3>
                <DonutChart data={patrimoineDonut} large />
                {kpis.debt > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-cap-error" />Dettes</span>
                      <span className="text-cap-error font-medium">-{fmt(kpis.debt)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account summary cards */}
            <div className="space-y-4">
              <h3 className="text-white font-semibold">Résumé par compte</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accounts.map(acct => {
                  const acctPositions = enrichedPositions.filter(p => p.account === acct.id);
                  const invested = acctPositions.reduce((s, p) => s + p.cost, 0);
                  const value = acctPositions.reduce((s, p) => s + p.value, 0);
                  const perf = value - invested;
                  const perfPct = invested > 0 ? (perf / invested) * 100 : 0;
                  return (
                    <div key={acct.id} className="glass-card p-5 group" data-testid={`acct-card-${acct.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3"><Logo name={acct.name} broker={acct.broker} size={8} /><div><p className="text-white font-semibold text-sm">{acct.name}</p><p className="text-cap-text-muted text-xs">{acct.type} · {acct.broker || '—'}</p></div></div>
                        <div className="flex items-center gap-3">
                          <div className="text-right"><p className="text-white font-display text-lg font-bold">{fmt(value)}</p><p className={`text-xs font-medium ${perf >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{perf >= 0 ? '+' : ''}{fmt(perf)} ({fmtP(perfPct)})</p></div>
                          <button onClick={() => deleteAccount(acct.id)} className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-acct-${acct.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-cap-text-muted"><span>Investi: {fmt(invested)}</span><span>·</span><span>{acctPositions.length} position{acctPositions.length !== 1 ? 's' : ''}</span></div>
                    </div>
                  );
                })}
                {savings.map(s => (
                  <div key={s.id} className="glass-card p-5">
                    <div className="flex items-center justify-between">
                      <div><p className="text-white font-semibold text-sm">{s.name}</p><p className="text-cap-text-muted text-xs">{s.institution || 'Épargne'} · {s.rate || 0}%</p></div>
                      <p className="text-cap-emerald font-display text-lg font-bold">{fmt(s.balance)}</p>
                    </div>
                  </div>
                ))}
                {realEstate.map(r => (
                  <div key={r.id} className="glass-card p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3"><Home className="w-8 h-8 text-[#7c3aed] opacity-60" /><div><p className="text-white font-semibold text-sm">{r.name}</p><p className="text-cap-text-muted text-xs">{r.type}</p></div></div>
                      <p className="text-[#7c3aed] font-display text-lg font-bold">{fmt(r.value)}</p>
                    </div>
                  </div>
                ))}
              </div>
              {accounts.length === 0 && savings.length === 0 && realEstate.length === 0 && (
                <p className="text-cap-text-muted text-sm text-center py-4">Ajoutez des comptes pour voir le résumé ici.</p>
              )}
            </div>

            {/* Detail donuts */}
            <div>
              <h3 className="text-white font-semibold mb-4">Diversification détaillée</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DonutChart data={donuts.byType} title="Par type d'actif" />
                <DonutChart data={donuts.byAccount} title="Par compte" />
                <DonutChart data={donuts.byGeo} title="Par géographie" />
                <DonutChart data={donuts.bySector} title="Par secteur" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ═══ TAB 2: INVESTISSEMENTS ═══ */}
        <TabsContent value="invest" data-testid="tab-content-invest">
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="bg-cap-bg border-white/10 text-white text-sm w-48"><SelectValue placeholder="Tous les comptes" /></SelectTrigger>
                  <SelectContent className="bg-cap-bg-elevated border-white/10">
                    <SelectItem value="all">Tous les comptes</SelectItem>
                    {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {accountFilter !== 'all' && (
                  <button onClick={() => deleteAccount(accountFilter)} className="p-2 rounded-lg border border-white/10 text-cap-text-muted hover:text-cap-error hover:border-cap-error/30 transition-all" title="Supprimer ce compte" data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <Button onClick={refreshAllPrices} disabled={loadingPrices} size="sm" variant="outline" className="border-white/10 text-cap-text-muted hover:text-white" data-testid="refresh-prices">
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingPrices ? 'animate-spin' : ''}`} /> {loadingPrices ? 'Chargement...' : 'Actualiser'}
                </Button>
              </div>
              {/* Dynamic KPIs for selected account */}
              {(() => {
                const totalValue = filtered.reduce((s, p) => s + p.value, 0);
                const totalCost = filtered.reduce((s, p) => s + p.cost, 0);
                const perfE = totalValue - totalCost;
                const perfP = totalCost > 0 ? (perfE / totalCost) * 100 : 0;
                return (
                  <div className="flex items-center gap-3" data-testid="invest-kpis">
                    {[
                      { l: 'Valeur totale', v: fmt(totalValue), c: 'text-white' },
                      { l: 'Total investi', v: fmt(totalCost), c: 'text-cap-text-secondary' },
                      { l: 'Perf €', v: `${perfE >= 0 ? '+' : ''}${fmt(perfE)}`, c: perfE >= 0 ? 'text-cap-emerald' : 'text-cap-error' },
                      { l: 'Perf %', v: fmtP(perfP), c: perfP >= 0 ? 'text-cap-emerald' : 'text-cap-error' },
                    ].map(k => (
                      <div key={k.l} className="px-3 py-1.5 bg-cap-bg rounded-lg">
                        <p className="text-cap-text-muted text-[10px]">{k.l}</p>
                        <p className={`font-display text-sm font-bold ${k.c}`}>{k.v}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div className="flex gap-2">
                <Dialog open={acctDlg} onOpenChange={setAcctDlg}>
                  <DialogTrigger asChild><Button size="sm" className="bg-cap-blue/20 text-cap-blue hover:bg-cap-blue/30" data-testid="add-account-btn"><Plus className="w-4 h-4 mr-1" /> Compte</Button></DialogTrigger>
                  <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm">
                    <DialogHeader><DialogTitle>Ajouter un compte</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Nom</Label><Input value={acctForm.name} onChange={e => setAcctForm({ ...acctForm, name: e.target.value })} placeholder="Mon PEA" className="bg-cap-bg border-white/10 text-white" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Type</Label><Select value={acctForm.type} onValueChange={v => setAcctForm({ ...acctForm, type: v, broker: '' })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">{acctForm.type === 'Crypto' ? 'Plateforme / Wallet' : (acctForm.type === 'Assurance Vie' ? 'Assureur / Banque' : 'Courtier / Banque')}</Label><InstitutionPicker value={acctForm.broker} onChange={v => setAcctForm({ ...acctForm, broker: v })} placeholder={acctForm.type === 'Crypto' ? 'Choisir une plateforme' : (acctForm.type === 'Assurance Vie' ? 'Choisir un assureur' : 'Choisir un courtier')} institutions={acctForm.type === 'Crypto' ? CRYPTO_PLATFORMS : (acctForm.type === 'Assurance Vie' ? ASSURANCE_VIE_PROVIDERS : undefined)} /></div>
                      <Button onClick={addAccount} className="w-full bg-cap-emerald text-cap-bg font-semibold">Créer le compte</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={posDlg} onOpenChange={setPosDlg}>
                  <DialogTrigger asChild><Button size="sm" className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30" data-testid="add-position-btn"><Plus className="w-4 h-4 mr-1" /> Position</Button></DialogTrigger>
                  <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-lg">
                    <DialogHeader><DialogTitle>Ajouter une position</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
                      <div className="grid grid-cols-2 gap-4 relative">
                        <div className="space-y-2">
                          <Label className="text-cap-text-secondary flex items-center gap-1.5">
                            Nom du titre
                            <TooltipProvider delayDuration={100}>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="text-cap-text-muted hover:text-cap-emerald transition-colors"
                                    data-testid="pos-name-info"
                                    aria-label="Aide sur la recherche de ticker"
                                  >
                                    <Info className="w-3.5 h-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-cap-bg-elevated border-white/10 text-white text-xs max-w-xs p-3 leading-relaxed">
                                  Tapez le nom de votre titre pour trouver le bon ticker automatiquement.
                                  Si plusieurs résultats semblent identiques, vérifiez le bon ticker sur{' '}
                                  <span className="text-cap-emerald font-medium">JustETF.com</span> (pour les ETF) ou sur le site de votre courtier.
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </Label>
                          <Input
                            value={posForm.name}
                            onChange={e => { setPosForm({ ...posForm, name: e.target.value }); setTickerQuery(e.target.value); setTickerOpen(true); }}
                            onFocus={() => { if (tickerResults.length > 0) setTickerOpen(true); }}
                            onBlur={() => setTimeout(() => setTickerOpen(false), 180)}
                            placeholder="S&P 500 BNP"
                            className="bg-cap-bg border-white/10 text-white"
                            autoComplete="off"
                            data-testid="pos-name-input"
                          />
                        </div>
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Ticker</Label><Input value={posForm.ticker} onChange={e => setPosForm({ ...posForm, ticker: e.target.value })} placeholder="ESE.PA" className="bg-cap-bg border-white/10 text-white" /><p className="text-cap-text-muted text-[10px] mt-1">Actions FR : MC.PA | US : AAPL | ETF : ESE.PA | Or : GC=F | Crypto : BTC, ETH...</p></div>
                        {/* Suggestion dropdown — anchored to the WHOLE row so it spans both columns. */}
                        {tickerOpen && tickerQuery.trim().length >= 3 && (tickerLoading || tickerResults.length > 0) && (() => {
                          // Sort: Paris-listed instruments (.PA) come first, then by relevance order.
                          const sorted = [...tickerResults].sort((a, b) => {
                            const ap = (a.symbol || '').toUpperCase().endsWith('.PA') ? 0 : 1;
                            const bp = (b.symbol || '').toUpperCase().endsWith('.PA') ? 0 : 1;
                            return ap - bp;
                          });
                          return (
                            <div
                              className="absolute left-0 right-0 top-[calc(100%+0.25rem)] z-50 bg-cap-bg-elevated border border-white/10 rounded-xl shadow-2xl max-h-80 overflow-auto"
                              data-testid="ticker-suggestions"
                            >
                              {tickerLoading && <div className="px-3 py-2 text-xs text-cap-text-muted">Recherche…</div>}
                              {!tickerLoading && sorted.length === 0 && <div className="px-3 py-2 text-xs text-cap-text-muted">Aucun résultat</div>}
                              {!tickerLoading && sorted.map((r, i) => {
                                const isParis = (r.symbol || '').toUpperCase().endsWith('.PA');
                                return (
                                  <button
                                    key={`${r.symbol}-${i}`}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      setPosForm(prev => ({ ...prev, name: r.name || r.symbol, ticker: r.symbol }));
                                      setTickerOpen(false);
                                      setTickerResults([]);
                                      setTickerQuery('');
                                    }}
                                    className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                                    data-testid={`ticker-suggestion-${i}`}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-white text-sm leading-snug break-words">{r.name || r.symbol}</p>
                                        <p className={`text-sm font-semibold mt-1 ${isParis ? 'text-cap-emerald' : 'text-cap-blue'}`}>
                                          {r.exchange || 'N/A'}{r.type ? ` · ${r.type}` : ''}
                                        </p>
                                      </div>
                                      <span className="text-cap-text-muted text-xs font-mono shrink-0 mt-0.5">{r.symbol}</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Code ISIN</Label><Input value={posForm.isin} onChange={e => setPosForm({ ...posForm, isin: e.target.value })} placeholder="FR0013412285" className="bg-cap-bg border-white/10 text-white" /></div>
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Type</Label><Select value={posForm.type} onValueChange={v => setPosForm({ ...posForm, type: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{POS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                      </div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Compte associé</Label><Select value={posForm.account} onValueChange={v => setPosForm({ ...posForm, account: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue placeholder="Choisir un compte" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Géographie</Label><Select value={posForm.geo} onValueChange={v => setPosForm({ ...posForm, geo: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{GEOS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Secteur</Label><Select value={posForm.sector} onValueChange={v => setPosForm({ ...posForm, sector: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Quantité</Label><Input type="number" value={posForm.quantity} onChange={e => setPosForm({ ...posForm, quantity: e.target.value })} className="bg-cap-bg border-white/10 text-white" step="any" /></div>
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Prix unitaire (€)</Label><Input type="number" value={posForm.price} onChange={e => setPosForm({ ...posForm, price: e.target.value })} className="bg-cap-bg border-white/10 text-white" step="0.01" /></div>
                        <div className="space-y-2"><Label className="text-cap-text-secondary">Date d'achat</Label><Input type="date" value={posForm.date} onChange={e => setPosForm({ ...posForm, date: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                      </div>
                      <Button onClick={addPosition} className="w-full bg-cap-emerald text-cap-bg font-semibold">Ajouter la position</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Positions Table */}
            <div className="mt-6">
            {filtered.length > 0 ? (
              <div className="glass-card overflow-hidden" data-testid="positions-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10">
                      <th className="text-left text-cap-text-muted font-medium px-4 py-3">Nom</th>
                      <th className="text-left text-cap-text-muted font-medium px-3 py-3">Type</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">Qté</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">PRU</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">Coût</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">Cours</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">Valeur</th>
                      <th className="text-right text-cap-text-muted font-medium px-3 py-3">Perf €</th>
                      <th className="text-right text-cap-text-muted font-medium px-4 py-3">Perf %</th>
                      <th className="px-2 py-3 w-10"></th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(p => (
                        <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors group/row" onClick={() => { setSelectedPos(p); setPosDetailOpen(true); }} data-testid={`pos-row-${p.id}`}>
                          <td className="px-4 py-3"><div className="flex items-center gap-2.5"><Logo name={p.name} ticker={p.ticker} type={p.type} /><div><p className="text-white font-medium text-sm">{p.name}</p>{p.ticker && <p className="text-cap-text-muted text-xs">{p.ticker}</p>}</div></div></td>
                          <td className="px-3 py-3"><span className="px-2 py-0.5 rounded text-xs bg-white/5 text-cap-text-secondary">{p.type}</span></td>
                          <td className="px-3 py-3 text-right text-white">{p.quantity}</td>
                          <td className="px-3 py-3 text-right text-cap-text-secondary">{fmtD(p.price)}</td>
                          <td className="px-3 py-3 text-right text-cap-text-secondary">{fmt(p.cost)}</td>
                          <td className="px-3 py-3 text-right">
                            {(() => {
                              const baseCur = (p.priceCurrency || 'EUR').replace('_UNCONVERTED', '');
                              const showFx = baseCur && baseCur !== 'EUR' && p.nativePrice != null;
                              const unconverted = p.priceCurrency && p.priceCurrency.endsWith('_UNCONVERTED');
                              const priceNode = (
                                <span className={`font-medium ${unconverted ? 'text-cap-warning' : 'text-white'} ${showFx ? 'underline decoration-dotted decoration-cap-text-muted underline-offset-4 cursor-help' : ''}`}>
                                  {fmtD(p.currentPrice)}
                                </span>
                              );
                              if (!showFx) return <>{priceNode}{p.isStale && <span className="block text-[9px] text-cap-text-muted">Dernier cours connu</span>}</>;
                              return (
                                <>
                                  <TooltipProvider delayDuration={150}>
                                    <UITooltip>
                                      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>{priceNode}</TooltipTrigger>
                                      <TooltipContent side="top" className="bg-cap-bg-elevated border-white/10 text-white text-xs">
                                        {unconverted ? (
                                          <span>{fmtD(p.nativePrice)} {baseCur} — taux FX indisponible</span>
                                        ) : (
                                          <span>{fmtD(p.nativePrice)} {baseCur} — converti via EUR/{baseCur} = {p.fxRate ? p.fxRate.toFixed(4) : '—'}</span>
                                        )}
                                      </TooltipContent>
                                    </UITooltip>
                                  </TooltipProvider>
                                  {p.isStale && <span className="block text-[9px] text-cap-text-muted">Dernier cours connu</span>}
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-3 text-right text-white font-semibold">{fmt(p.value)}</td>
                          <td className={`px-3 py-3 text-right font-medium ${p.perfEuro >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{p.perfEuro >= 0 ? '+' : ''}{fmt(p.perfEuro)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${p.perfPct >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{fmtP(p.perfPct)}</td>
                          <td className="px-2 py-3"><button onClick={(e) => openEditPosition(p, e)} className="p-1.5 rounded text-cap-text-muted hover:text-cap-blue opacity-0 group-hover/row:opacity-100 transition-all" data-testid={`edit-pos-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <BarChart3 className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold text-white mb-2">Aucune position</h3>
                <p className="text-cap-text-secondary mb-4">Commencez par ajouter un compte, puis vos positions.</p>
              </div>
            )}
            </div>

            {/* Confirmation modale suppression compte */}
            <Dialog open={!!deleteAcctConfirm} onOpenChange={(open) => { if (!open) setDeleteAcctConfirm(null); }}>
              <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm">
                <DialogHeader><DialogTitle>Supprimer ce compte</DialogTitle></DialogHeader>
                <p className="text-cap-text-secondary text-sm mt-2">Supprimer ce compte et toutes ses positions ? Cette action est irréversible.</p>
                <div className="flex items-center justify-end gap-3 mt-4">
                  <Button variant="outline" size="sm" className="border-white/10 text-cap-text-muted hover:text-white" onClick={() => setDeleteAcctConfirm(null)} data-testid="cancel-delete-acct">Annuler</Button>
                  <Button size="sm" className="bg-cap-error text-white hover:bg-red-600" onClick={confirmDeleteAccount} data-testid="confirm-delete-acct">Confirmer</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* ═══ TAB 3: ÉPARGNE ═══ */}
        <TabsContent value="savings" data-testid="tab-content-savings">
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 flex-wrap">
                {[
                  { label: 'Total épargne', value: kpis.savings, color: '#00e5a0' },
                  { label: 'Intérêts/an estimés', value: savings.reduce((s, sv) => s + (parseFloat(sv.balance) || 0) * ((parseFloat(sv.rate) || 0) / 100), 0), color: '#4f8eff' },
                  { label: 'Comptes', value: savings.length, color: '#7c3aed', raw: true }
                ].map(k => (
                  <div key={k.label} className="px-4 py-2 bg-cap-bg rounded-lg">
                    <p className="text-cap-text-muted text-xs">{k.label}</p>
                    <p className="font-display text-lg font-bold" style={{ color: k.color }}>{k.raw ? k.value : fmt(k.value)}</p>
                  </div>
                ))}
              </div>
              <Dialog open={savDlg} onOpenChange={setSavDlg}>
                <DialogTrigger asChild><Button size="sm" className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30" data-testid="add-savings-btn"><Plus className="w-4 h-4 mr-1" /> Compte épargne</Button></DialogTrigger>
                <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Ajouter un compte épargne</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-cap-text-secondary">Catégorie</Label>
                      <Select value={savForm.category} onValueChange={v => setSavForm({ ...savForm, category: v, accountType: '', customType: '', institution: '' })}>
                        <SelectTrigger className="bg-cap-bg border-white/10 text-white" data-testid="sav-category"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-cap-bg-elevated border-white/10 text-white">
                          <SelectItem value="personnelle">Épargne personnelle</SelectItem>
                          <SelectItem value="salariale">Épargne salariale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-cap-text-secondary">Type de compte</Label>
                      <Select value={savForm.accountType} onValueChange={v => setSavForm({ ...savForm, accountType: v, customType: '' })}>
                        <SelectTrigger className="bg-cap-bg border-white/10 text-white" data-testid="sav-account-type"><SelectValue placeholder="Choisir un type" /></SelectTrigger>
                        <SelectContent className="bg-cap-bg-elevated border-white/10 text-white">
                          {(savForm.category === 'salariale' ? TYPES_EPARGNE_SALARIALE : TYPES_EPARGNE_PERSONNELLE).map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savForm.accountType === 'Autre' && (
                        <Input value={savForm.customType} onChange={e => setSavForm({ ...savForm, customType: e.target.value })} placeholder="Préciser le type" className="bg-cap-bg border-white/10 text-white mt-2" data-testid="sav-custom-type" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-cap-text-secondary">Établissement</Label>
                      <InstitutionPicker value={savForm.institution} onChange={v => setSavForm({ ...savForm, institution: v })} placeholder={savForm.category === 'salariale' ? "Choisir un gestionnaire" : "Choisir une banque"} institutions={savForm.category === 'salariale' ? EPARGNE_SALARIALE_INSTITUTIONS : BANQUES_FR} />
                    </div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Nom du compte (optionnel)</Label><Input value={savForm.name} onChange={e => setSavForm({ ...savForm, name: e.target.value })} placeholder="Ex: Livret A Crédit Agricole" className="bg-cap-bg border-white/10 text-white" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Taux annuel (%)</Label><Input type="number" value={savForm.rate} onChange={e => setSavForm({ ...savForm, rate: e.target.value })} placeholder="3" className="bg-cap-bg border-white/10 text-white" step="0.1" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Solde actuel (€)</Label><Input type="number" value={savForm.balance} onChange={e => setSavForm({ ...savForm, balance: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    </div>
                    {savForm.category === 'salariale' && (
                      <div className="space-y-2">
                        <Label className="text-cap-text-secondary">Abondement employeur (%) <span className="text-cap-text-muted text-xs">— optionnel</span></Label>
                        <Input
                          type="number"
                          value={savForm.employerMatchPct}
                          onChange={e => setSavForm({ ...savForm, employerMatchPct: e.target.value })}
                          placeholder="Ex: 100 (= 100% du versement abondé)"
                          className="bg-cap-bg border-white/10 text-white"
                          step="1"
                          min="0"
                          data-testid="sav-employer-match"
                        />
                        <p className="text-cap-text-muted text-xs">Chaque versement saisi dans Gestion Financière sera majoré de ce pourcentage.</p>
                      </div>
                    )}
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Date d'ouverture</Label><Input type="date" value={savForm.date} onChange={e => setSavForm({ ...savForm, date: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    <Button onClick={addSaving} className="w-full bg-cap-emerald text-cap-bg font-semibold" data-testid="sav-submit">Ajouter</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-5">
              {savings.length > 0 ? savings.map(s => {
                const interest = (parseFloat(s.balance) || 0) * ((parseFloat(s.rate) || 0) / 100);
                const linked = savLinks[s.id];
                const goal = objectives.find(o => o.id === linked);
                const matchPct = parseFloat(s.employerMatchPct) || 0;
                const matchTotal = parseFloat(s.employerMatchTotal) || 0;
                const allocs = Array.isArray(s.allocations) ? s.allocations : [];
                const totalAlloc = allocs.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                const nonAlloc = Math.max(0, (parseFloat(s.balance) || 0) - totalAlloc);
                return (
                  <div key={s.id} className="glass-card p-5 mb-4 group" data-testid={`saving-${s.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div><p className="text-white font-semibold">{s.name}</p><p className="text-cap-text-muted text-xs">{s.institution || '—'} · Taux : {s.rate || 0}%{s.date && ` · Ouvert le ${s.date}`}</p></div>
                      <div className="flex items-center gap-3">
                        <div className="text-right"><p className="font-display text-xl font-bold text-cap-emerald">{fmt(s.balance)}</p><p className="text-cap-text-muted text-xs">Intérêts/an : {fmt(interest)}</p></div>
                        <button onClick={() => deleteSaving(s.id)} className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {matchPct > 0 && (
                      <div className="mt-2 px-3 py-2 bg-cap-blue/10 border border-cap-blue/20 rounded-lg flex items-center justify-between" data-testid={`saving-employer-match-${s.id}`}>
                        <span className="text-cap-blue text-xs font-medium">Abondement employeur : {matchPct}%</span>
                        <span className="text-cap-blue text-xs">Total perçu : <span className="font-semibold">{fmt(matchTotal)}</span></span>
                      </div>
                    )}
                    {/* Allocations */}
                    <div className="mt-3 pt-3 border-t border-white/5" data-testid={`saving-allocations-${s.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-cap-text-muted text-xs uppercase tracking-wide">Allocations</span>
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-cap-text-secondary hover:text-cap-emerald" onClick={() => openAllocDlg(s)} data-testid={`saving-alloc-manage-${s.id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Gérer
                        </Button>
                      </div>
                      {allocs.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {allocs.map(a => {
                            const linkedGoal = objectives.find(o => o.id === a.objectiveId);
                            return (
                              <div key={a.id} className="flex items-center justify-between text-xs">
                                <span className="text-white truncate">
                                  {a.name}
                                  {linkedGoal && <span className="ml-1.5 text-cap-blue">→ {linkedGoal.name}</span>}
                                </span>
                                <span className="text-cap-emerald font-medium shrink-0 ml-2">{fmt(a.amount)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="text-cap-text-muted">Non alloué</span>
                        <span className={nonAlloc > 0.005 ? 'text-white font-medium' : 'text-cap-text-muted'}>{fmt(nonAlloc)}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-2">
                      <Button size="sm" variant="outline" className="h-6 text-xs border-white/10 text-cap-text-muted" onClick={() => { const v = prompt('Nouveau solde (€) :', s.balance); if (v !== null) updateSavBalance(s.id, v); }}>Mettre à jour le solde</Button>
                      {allocs.length > 0 ? (
                        <span className="text-cap-text-muted text-[10px] italic">Allocations actives — lien direct ignoré</span>
                      ) : linked ? (
                        <span className="flex items-center gap-1 text-cap-blue"><Link2 className="w-3 h-3" /><span className="truncate max-w-[120px]">{goal?.name || '—'}</span><button onClick={() => unlinkSav(s.id)} className="text-cap-text-muted hover:text-cap-error"><Unlink className="w-3 h-3" /></button></span>
                      ) : objectives.length > 0 && (
                        <Select onValueChange={v => linkSav(s.id, v)}><SelectTrigger className="h-6 w-auto border-none bg-transparent text-cap-blue text-xs p-0 gap-1"><Link2 className="w-3 h-3" /><span>Lier à un objectif</span></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{objectives.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="glass-card p-12 text-center">
                  <Wallet className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Aucun compte épargne</h3>
                  <p className="text-cap-text-secondary">Ajoutez vos livrets et comptes pour suivre votre épargne.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ═══ TAB 4: IMMOBILIER ═══ */}
        <TabsContent value="realestate" data-testid="tab-content-realestate">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Valeur immobilière</p>
                  <p className="font-display text-lg font-bold text-[#7c3aed]">{fmt(kpis.re)}</p>
                </div>
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Plus-value latente</p>
                  <p className={`font-display text-lg font-bold ${realEstate.reduce((s, r) => s + ((parseFloat(r.value) || 0) - (parseFloat(r.purchase_price) || 0)), 0) >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{fmt(realEstate.reduce((s, r) => s + ((parseFloat(r.value) || 0) - (parseFloat(r.purchase_price) || parseFloat(r.value) || 0)), 0))}</p>
                </div>
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Biens</p>
                  <p className="font-display text-lg font-bold text-[#7c3aed]">{realEstate.length}</p>
                </div>
              </div>
              <Dialog open={reDlg} onOpenChange={setReDlg}>
                <DialogTrigger asChild><Button size="sm" className="bg-[#7c3aed]/20 text-[#7c3aed] hover:bg-[#7c3aed]/30" data-testid="add-realestate-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un bien</Button></DialogTrigger>
                <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm">
                  <DialogHeader><DialogTitle>Ajouter un bien immobilier</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Nom</Label><Input value={reForm.name} onChange={e => setReForm({ ...reForm, name: e.target.value })} placeholder="Résidence Le Mans" className="bg-cap-bg border-white/10 text-white" /></div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Type</Label><Select value={reForm.type} onValueChange={v => setReForm({ ...reForm, type: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{RE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Valeur actuelle (€)</Label><Input type="number" value={reForm.value} onChange={e => setReForm({ ...reForm, value: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Prix d'achat (€)</Label><Input type="number" value={reForm.purchase_price} onChange={e => setReForm({ ...reForm, purchase_price: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    </div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Date d'acquisition</Label><Input type="date" value={reForm.date} onChange={e => setReForm({ ...reForm, date: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    {debts.length > 0 && (
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Crédit associé (optionnel)</Label><Select value={reForm.linked_debt || 'none'} onValueChange={v => setReForm({ ...reForm, linked_debt: v === 'none' ? '' : v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue placeholder="Aucun" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10"><SelectItem value="none">Aucun</SelectItem>{debts.map(d => { const a = computeAmortization(d.total_amount, d.monthly_payment, d.interest_rate || 0, d.start_date, d.prepayments || []); return <SelectItem key={d.id} value={d.id}>{d.name} ({fmt(a.capitalRestant)})</SelectItem>; })}</SelectContent></Select></div>
                    )}
                    <Button onClick={addRealEstate} className="w-full bg-cap-emerald text-cap-bg font-semibold">Ajouter</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {realEstate.length > 0 ? realEstate.map(r => {
                const pv = (parseFloat(r.value) || 0) - (parseFloat(r.purchase_price) || parseFloat(r.value) || 0);
                const pvPct = (parseFloat(r.purchase_price) || 0) > 0 ? (pv / parseFloat(r.purchase_price)) * 100 : 0;
                const linkedDebt = debts.find(d => d.id === r.linked_debt);
                return (
                  <div key={r.id} className="glass-card p-6 group" data-testid={`re-${r.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center"><Home className="w-6 h-6 text-[#7c3aed]" /></div>
                        <div>
                          <p className="text-white font-semibold text-lg">{r.name}</p>
                          <p className="text-cap-text-muted text-sm">{r.type}{r.date && ` · Acquis le ${r.date}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-display text-2xl font-bold text-[#7c3aed]">{fmt(r.value)}</p>
                          {r.purchase_price > 0 && <p className={`text-xs font-medium ${pv >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{pv >= 0 ? '+' : ''}{fmt(pv)} ({fmtP(pvPct)})</p>}
                        </div>
                        <button onClick={() => deleteRE(r.id)} className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-cap-text-muted">
                      {r.purchase_price > 0 && <span>Acheté : {fmt(r.purchase_price)}</span>}
                      {linkedDebt && (() => { const la = computeAmortization(linkedDebt.total_amount, linkedDebt.monthly_payment, linkedDebt.interest_rate || 0, linkedDebt.start_date, linkedDebt.prepayments || []); return <span className="flex items-center gap-1 text-cap-blue"><Link2 className="w-3 h-3" />{linkedDebt.name} (reste {fmt(la.capitalRestant)})</span>; })()}
                    </div>
                  </div>
                );
              }) : (
                <div className="glass-card p-12 text-center">
                  <Home className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Aucun bien immobilier</h3>
                  <p className="text-cap-text-secondary">Ajoutez vos biens pour suivre votre patrimoine immobilier.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ═══ TAB 5: AUTRES ACTIFS ═══ */}
        <TabsContent value="otherassets" data-testid="tab-content-otherassets">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Valeur totale</p>
                  <p className="font-display text-lg font-bold text-[#f97316]">{fmt(kpis.oa)}</p>
                </div>
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Plus-value latente</p>
                  <p className={`font-display text-lg font-bold ${otherAssets.reduce((s, a) => s + ((parseFloat(a.value) || 0) - (parseFloat(a.purchase_price) || 0)), 0) >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{fmt(otherAssets.reduce((s, a) => s + ((parseFloat(a.value) || 0) - (parseFloat(a.purchase_price) || parseFloat(a.value) || 0)), 0))}</p>
                </div>
                <div className="px-4 py-2 bg-cap-bg rounded-lg">
                  <p className="text-cap-text-muted text-xs">Actifs</p>
                  <p className="font-display text-lg font-bold text-[#f97316]">{otherAssets.length}</p>
                </div>
              </div>
              <Dialog open={oaDlg} onOpenChange={setOaDlg}>
                <DialogTrigger asChild><Button size="sm" className="bg-[#f97316]/20 text-[#f97316] hover:bg-[#f97316]/30" data-testid="add-otherasset-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un actif</Button></DialogTrigger>
                <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm">
                  <DialogHeader><DialogTitle>Ajouter un actif</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Nom</Label><Input value={oaForm.name} onChange={e => setOaForm({ ...oaForm, name: e.target.value })} placeholder="Rolex Submariner" className="bg-cap-bg border-white/10 text-white" /></div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Catégorie</Label><Select value={oaForm.category} onValueChange={v => setOaForm({ ...oaForm, category: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{ASSET_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Valeur estimée (€)</Label><Input type="number" value={oaForm.value} onChange={e => setOaForm({ ...oaForm, value: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Prix d'achat (€)</Label><Input type="number" value={oaForm.purchase_price} onChange={e => setOaForm({ ...oaForm, purchase_price: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    </div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Date d'acquisition</Label><Input type="date" value={oaForm.date} onChange={e => setOaForm({ ...oaForm, date: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                    <Button onClick={addOtherAsset} className="w-full bg-cap-emerald text-cap-bg font-semibold">Ajouter</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {otherAssets.length > 0 ? otherAssets.map(a => {
                const pv = (parseFloat(a.value) || 0) - (parseFloat(a.purchase_price) || parseFloat(a.value) || 0);
                const pvPct = (parseFloat(a.purchase_price) || 0) > 0 ? (pv / parseFloat(a.purchase_price)) * 100 : 0;
                const catIcon = { 'Véhicule': Car, 'Montre': Gem, 'Art': Gem, 'Or': Gem }[a.category] || Gem;
                const CatIcon = catIcon;
                return (
                  <div key={a.id} className="glass-card p-6 group" data-testid={`oa-${a.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#f97316]/10 flex items-center justify-center"><CatIcon className="w-6 h-6 text-[#f97316]" /></div>
                        <div>
                          <p className="text-white font-semibold text-lg">{a.name}</p>
                          <p className="text-cap-text-muted text-sm">{a.category}{a.date && ` · Acquis le ${a.date}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-display text-2xl font-bold text-[#f97316]">{fmt(a.value)}</p>
                          {a.purchase_price > 0 && <p className={`text-xs font-medium ${pv >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{pv >= 0 ? '+' : ''}{fmt(pv)} ({fmtP(pvPct)})</p>}
                        </div>
                        <button onClick={() => deleteOA(a.id)} className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    {a.purchase_price > 0 && <p className="text-xs text-cap-text-muted">Acheté : {fmt(a.purchase_price)}</p>}
                  </div>
                );
              }) : (
                <div className="glass-card p-12 text-center">
                  <Gem className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Aucun autre actif</h3>
                  <p className="text-cap-text-secondary">Ajoutez vos véhicules, montres, oeuvres d'art, or, etc.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ═══ TAB 6: DETTES ═══ */}
        <TabsContent value="debts" data-testid="tab-content-debts">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="px-4 py-2 bg-cap-bg rounded-lg">
                <p className="text-cap-text-muted text-xs">Capital restant dû</p>
                <p className="font-display text-lg font-bold text-cap-error">{fmt(kpis.debt)}</p>
              </div>
              <Dialog open={debtDlg} onOpenChange={setDebtDlg}>
                <DialogTrigger asChild><Button size="sm" className="bg-cap-error/20 text-cap-error hover:bg-cap-error/30" data-testid="add-debt-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle dette</Button></DialogTrigger>
                <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-md">
                  <DialogHeader><DialogTitle>Ajouter une dette</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Nom</Label><Input value={debtForm.name} onChange={e => setDebtForm({ ...debtForm, name: e.target.value })} placeholder="Crédit immobilier Appartement Paris" className="bg-cap-bg border-white/10 text-white" data-testid="debt-name" /></div>
                    <div className="space-y-2"><Label className="text-cap-text-secondary">Type de crédit</Label>
                      <Select value={debtForm.type} onValueChange={v => setDebtForm({ ...debtForm, type: v })}>
                        <SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-cap-bg-elevated border-white/10">
                          <SelectItem value="immobilier">Crédit immobilier</SelectItem>
                          <SelectItem value="consommation">Crédit consommation</SelectItem>
                          <SelectItem value="auto">Crédit auto</SelectItem>
                          <SelectItem value="etudiant">Prêt étudiant</SelectItem>
                          <SelectItem value="autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Montant emprunté (€)</Label><Input type="number" value={debtForm.total_amount} onChange={e => setDebtForm({ ...debtForm, total_amount: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="debt-total" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Mensualité (€)</Label><Input type="number" value={debtForm.monthly_payment} onChange={e => setDebtForm({ ...debtForm, monthly_payment: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="debt-monthly" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Taux annuel (%)</Label><Input type="number" value={debtForm.interest_rate} onChange={e => setDebtForm({ ...debtForm, interest_rate: e.target.value })} className="bg-cap-bg border-white/10 text-white" step="0.01" data-testid="debt-rate" /></div>
                      <div className="space-y-2"><Label className="text-cap-text-secondary">Date de début</Label><Input type="date" value={debtForm.start_date} onChange={e => setDebtForm({ ...debtForm, start_date: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="debt-start" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-cap-text-secondary">Quote-part (%) <span className="text-cap-text-muted text-xs">— optionnel, par défaut 100%</span></Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={debtForm.ownership_pct}
                        onChange={e => setDebtForm({ ...debtForm, ownership_pct: e.target.value })}
                        placeholder="Ex: 50 pour un crédit partagé à 2"
                        className="bg-cap-bg border-white/10 text-white"
                        data-testid="debt-ownership"
                      />
                      <p className="text-cap-text-muted text-xs">Si renseigné, seule ta part de la mensualité et du capital restant sera comptabilisée dans ton patrimoine.</p>
                    </div>
                    {debtForm.monthly_payment && <p className="text-cap-blue/80 text-xs p-2 bg-cap-blue/10 rounded-lg flex items-center gap-1.5">La mensualité de {fmt(parseFloat(debtForm.monthly_payment) || 0)}/mois sera automatiquement ajoutée à tes charges fixes dans Gestion Financière.</p>}
                    <Button onClick={addDebt} className="w-full bg-cap-emerald text-cap-bg font-semibold" data-testid="submit-debt">Ajouter</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="space-y-4">
              {debts.length > 0 ? debts.map(debt => {
                const prepays = Array.isArray(debt.prepayments) ? debt.prepayments : [];
                const amort = computeAmortization(debt.total_amount, debt.monthly_payment, debt.interest_rate || 0, debt.start_date, prepays);
                const typeLabel = { immobilier: 'Immobilier', consommation: 'Consommation', auto: 'Auto', etudiant: 'Étudiant', autre: 'Autre' }[debt.debt_type || debt.type] || 'Crédit';
                const sharePctRaw = parseFloat(debt.ownership_pct);
                const sharePct = Number.isFinite(sharePctRaw) && sharePctRaw > 0 ? sharePctRaw : 100;
                const isShared = sharePct < 100;
                const effectiveMonthly = (parseFloat(debt.monthly_payment) || 0) * (sharePct / 100);
                const effectiveRestant = amort.capitalRestant * (sharePct / 100);
                const totalPrepaid = prepays.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
                return (
                  <div key={debt.id} className="glass-card p-6 group" data-testid={`debt-${debt.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-semibold text-lg">{debt.name}</h3>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-cap-text-muted">{typeLabel}</span>
                          {isShared && <span className="text-[10px] px-2 py-0.5 rounded bg-cap-blue/15 text-cap-blue font-medium" data-testid={`debt-share-${debt.id}`}>Quote-part {sharePct}%</span>}
                        </div>
                        <p className="text-cap-text-muted text-sm">
                          Mensualité: {fmt(effectiveMonthly)}{isShared && <span className="text-cap-text-muted/70"> / {fmt(debt.monthly_payment)} total</span>} · Taux: {debt.interest_rate || 0}% · Début: {debt.start_date}
                        </p>
                      </div>
                      <button onClick={() => deleteDebt(debt.id)} className="p-2 rounded-lg text-cap-text-muted hover:text-cap-error opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-debt-${debt.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {/* Progress bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-cap-emerald">Remboursé: {fmt(amort.capitalRembourse)}</span>
                        <span className="text-cap-error">Restant: {fmt(amort.capitalRestant)}{isShared && <span className="text-cap-text-muted/70 text-xs"> · ta part {fmt(effectiveRestant)}</span>}</span>
                      </div>
                      <div className="h-4 bg-cap-bg rounded-full overflow-hidden flex">
                        <div className="h-full bg-cap-emerald transition-all" style={{ width: `${Math.min(100, amort.pctRembourse)}%` }} />
                        <div className="h-full bg-cap-error" style={{ width: `${Math.max(0, 100 - amort.pctRembourse)}%` }} />
                      </div>
                      <div className="text-center text-cap-text-secondary text-sm font-medium">{amort.pctRembourse.toFixed(1)}% remboursé</div>
                    </div>
                    {/* Computed details */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div className="p-3 bg-cap-bg rounded-xl text-center">
                        <p className="text-cap-text-muted text-[10px]">Mois restants</p>
                        <p className="text-white font-display font-bold">{amort.moisRestants >= 9999 ? '∞' : amort.moisRestants}</p>
                        {amort.moisRestants < 9999 && <p className="text-cap-text-muted text-[10px]">({Math.ceil(amort.moisRestants / 12)} ans)</p>}
                      </div>
                      <div className="p-3 bg-cap-bg rounded-xl text-center">
                        <p className="text-cap-text-muted text-[10px]">Mensualité {isShared ? '(ta part)' : 'restante'}</p>
                        <p className="text-white font-display font-bold">{fmt(effectiveMonthly)}</p>
                      </div>
                      <div className="p-3 bg-cap-bg rounded-xl text-center">
                        <p className="text-cap-text-muted text-[10px]">Coût total intérêts</p>
                        <p className="text-cap-error font-display font-bold">{fmt(amort.totalInterets)}</p>
                      </div>
                    </div>
                    {/* Prepayments */}
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs border-cap-emerald/30 text-cap-emerald hover:bg-cap-emerald/10"
                        onClick={() => setPrepayDlg({ open: true, debt, amount: '', date: format(new Date(), 'yyyy-MM-dd') })}
                        data-testid={`debt-prepay-btn-${debt.id}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Remboursement anticipé
                      </Button>
                      {prepays.length > 0 && (
                        <span className="text-xs text-cap-text-muted" data-testid={`debt-prepay-count-${debt.id}`}>
                          {prepays.length} versement{prepays.length > 1 ? 's' : ''} anticipé{prepays.length > 1 ? 's' : ''} — total {fmt(totalPrepaid)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="glass-card p-12 text-center">
                  <CreditCard className="w-16 h-16 text-cap-text-muted mx-auto mb-4" />
                  <h3 className="font-display text-xl font-semibold text-white mb-2">Aucune dette</h3>
                  <p className="text-cap-text-secondary">Félicitations ! Ou ajoutez vos dettes pour suivre leur remboursement.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ═══ ALLOCATION MANAGER MODAL ═══ */}
      <Dialog open={allocDlg.open} onOpenChange={(o) => { if (!o) setAllocDlg({ open: false, savingId: null, items: [] }); }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-xl w-[92vw] max-h-[85vh] overflow-y-auto" data-testid="allocation-modal">
          <DialogHeader><DialogTitle>Gérer les allocations</DialogTitle></DialogHeader>
          {(() => {
            const acct = savings.find(s => s.id === allocDlg.savingId);
            if (!acct) return null;
            const balance = parseFloat(acct.balance) || 0;
            const totalAlloc = allocDlg.items.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
            const nonAlloc = balance - totalAlloc;
            const over = nonAlloc < -0.005;
            return (
              <div className="space-y-4 mt-2">
                <div className="text-sm text-cap-text-secondary">
                  <span className="text-white font-medium">{acct.name}</span>
                  <span className="mx-2 text-cap-text-muted">·</span>
                  <span>Solde : <span className="text-cap-emerald font-semibold">{fmt(balance)}</span></span>
                </div>
                <div className="space-y-2">
                  {allocDlg.items.length === 0 && (
                    <p className="text-cap-text-muted text-sm text-center py-4">Aucune allocation. Ajoutez-en pour structurer ton épargne.</p>
                  )}
                  {allocDlg.items.map((a, i) => (
                    <div key={a.id} className="grid grid-cols-12 gap-2 items-end pb-3 border-b border-white/5 last:border-0" data-testid={`alloc-row-${i}`}>
                      <div className="col-span-5 space-y-1">
                        <Label className="text-cap-text-muted text-[10px] uppercase tracking-wide">Nom</Label>
                        <Input
                          value={a.name}
                          onChange={e => updateAllocItem(i, { name: e.target.value })}
                          placeholder="Épargne sécurité"
                          className="bg-cap-bg border-white/10 text-white text-sm h-9"
                          data-testid={`alloc-name-${i}`}
                        />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-cap-text-muted text-[10px] uppercase tracking-wide">Montant</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={a.amount}
                          onChange={e => updateAllocItem(i, { amount: e.target.value })}
                          placeholder="0"
                          className="bg-cap-bg border-white/10 text-white text-sm h-9"
                          data-testid={`alloc-amount-${i}`}
                        />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-cap-text-muted text-[10px] uppercase tracking-wide">Objectif lié</Label>
                        <Select value={a.objectiveId || 'none'} onValueChange={v => updateAllocItem(i, { objectiveId: v === 'none' ? null : v })}>
                          <SelectTrigger className="bg-cap-bg border-white/10 text-white text-xs h-9" data-testid={`alloc-objective-${i}`}>
                            <SelectValue placeholder="Aucun" />
                          </SelectTrigger>
                          <SelectContent className="bg-cap-bg-elevated border-white/10 text-white">
                            <SelectItem value="none">Aucun</SelectItem>
                            {objectives.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAllocItem(i)}
                        className="col-span-1 p-1.5 text-cap-text-muted hover:text-cap-error justify-self-center h-9"
                        data-testid={`alloc-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="w-full border-dashed border-white/10 text-cap-text-secondary hover:text-cap-emerald" onClick={addAllocItem} data-testid="alloc-add">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter une allocation
                  </Button>
                </div>
                <div className={`p-3 rounded-lg flex items-center justify-between ${over ? 'bg-cap-error/10 border border-cap-error/30' : 'bg-cap-bg'}`} data-testid="alloc-summary">
                  <div className="text-sm">
                    <p className="text-cap-text-muted text-xs">Alloué / Solde</p>
                    <p className={over ? 'text-cap-error font-semibold' : 'text-white font-semibold'}>{fmt(totalAlloc)} / {fmt(balance)}</p>
                  </div>
                  <div className="text-sm text-right">
                    <p className="text-cap-text-muted text-xs">{over ? 'Dépassement' : 'Non alloué'}</p>
                    <p className={over ? 'text-cap-error font-semibold' : 'text-cap-emerald font-semibold'}>{fmt(Math.abs(nonAlloc))}</p>
                  </div>
                </div>
                {over && (
                  <p className="text-xs text-cap-error">⚠ Les allocations dépassent le solde du compte. Réduisez les montants ou augmentez le solde avant d'enregistrer.</p>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={() => setAllocDlg({ open: false, savingId: null, items: [] })}>Annuler</Button>
                  <Button
                    className="bg-cap-emerald text-cap-bg hover:bg-cap-emerald/90 disabled:opacity-40 disabled:pointer-events-none"
                    disabled={over}
                    onClick={saveAllocations}
                    data-testid="alloc-save"
                  >
                    Enregistrer
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ═══ EARLY REPAYMENT MODAL ═══ */}
      <Dialog open={prepayDlg.open} onOpenChange={(o) => { if (!o) setPrepayDlg({ open: false, debt: null, amount: '', date: '' }); }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-md" data-testid="prepay-modal">
          <DialogHeader><DialogTitle>Remboursement anticipé</DialogTitle></DialogHeader>
          {prepayDlg.debt && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-cap-text-secondary">
                <span className="text-white font-medium">{prepayDlg.debt.name}</span>
                <span className="mx-2 text-cap-text-muted">·</span>
                <span>Capital restant : {fmt(computeAmortization(prepayDlg.debt.total_amount, prepayDlg.debt.monthly_payment, prepayDlg.debt.interest_rate || 0, prepayDlg.debt.start_date, prepayDlg.debt.prepayments || []).capitalRestant)}</span>
              </p>
              <div className="space-y-2">
                <Label className="text-cap-text-secondary">Montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  autoFocus
                  value={prepayDlg.amount}
                  onChange={e => setPrepayDlg({ ...prepayDlg, amount: e.target.value })}
                  className="bg-cap-bg border-white/10 text-white"
                  data-testid="prepay-amount"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-cap-text-secondary">Date</Label>
                <Input
                  type="date"
                  lang="fr"
                  value={prepayDlg.date}
                  onChange={e => setPrepayDlg({ ...prepayDlg, date: e.target.value })}
                  className="bg-cap-bg border-white/10 text-white"
                  data-testid="prepay-date"
                />
              </div>
              <p className="text-xs text-cap-text-muted">Le capital restant dû et l'échéancier seront recalculés automatiquement.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={() => setPrepayDlg({ open: false, debt: null, amount: '', date: '' })}>Annuler</Button>
                <Button
                  className="bg-cap-emerald text-cap-bg hover:bg-cap-emerald/90"
                  onClick={async () => {
                    const ok = await addPrepayment(prepayDlg.debt, prepayDlg.amount, prepayDlg.date);
                    if (ok) setPrepayDlg({ open: false, debt: null, amount: '', date: '' });
                  }}
                  data-testid="prepay-submit"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attribution */}
      <p className="text-center text-[10px] text-cap-text-muted/40 pt-4">Logos by <a href="https://logos.apistemic.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-cap-text-muted/60">apistemic logos</a></p>

      {/* ═══ POSITION DETAIL MODAL (centered, 80%w x 85%h) ═══ */}
      <Dialog open={posDetailOpen} onOpenChange={setPosDetailOpen}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white p-0 max-w-[80vw] w-[80vw] max-h-[90vh] overflow-y-auto" data-testid="pos-detail-modal">
          <DialogTitle className="sr-only">Détail de la position</DialogTitle>
          {selectedPos && (() => {
            const ep = enrichedPositions.find(p => p.id === selectedPos.id) || selectedPos;
            const txs = ep.transactions || [];
            // Compute Y domain to always include PRU
            const prices = filteredHistory.map(d => d.close);
            const yMin = prices.length > 0 ? Math.min(...prices, ep.price) * 0.97 : 0;
            const yMax = prices.length > 0 ? Math.max(...prices, ep.price) * 1.03 : 100;
            // Build scatter-ready tx markers — find nearest date in history
            const txOnChart = txs.map(tx => {
              let pt = filteredHistory.find(d => d.date === tx.date);
              if (!pt && filteredHistory.length > 0) {
                const txTime = new Date(tx.date).getTime();
                let best = null, bestDiff = Infinity;
                for (const d of filteredHistory) {
                  const diff = Math.abs(new Date(d.date).getTime() - txTime);
                  if (diff < bestDiff) { bestDiff = diff; best = d; }
                }
                if (best && bestDiff < 5 * 86400000) pt = best;
              }
              return pt ? { ...tx, chartDate: pt.date, chartY: pt.close } : null;
            }).filter(Boolean);

            return (
              <div className="flex flex-col">
                {/* Modal header */}
                <div className="flex items-center justify-between px-8 py-5 border-b border-white/5 sticky top-0 bg-cap-bg-elevated z-10">
                  <div className="flex items-center gap-4">
                    <Logo name={ep.name} ticker={ep.ticker} type={ep.type} size={12} />
                    <div>
                      <h2 className="font-display text-xl font-bold text-white">{ep.name}</h2>
                      <p className="text-cap-text-muted text-sm">{ep.ticker}{ep.isin && ` · ${ep.isin}`} · {ep.accountName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-display text-2xl font-bold text-white">{fmtD(ep.currentPrice)}</p>
                      <p className={`text-sm font-medium ${ep.change >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{ep.change >= 0 ? '+' : ''}{ep.change.toFixed(2)}% aujourd'hui</p>
                    </div>
                    <Button onClick={() => deletePosition(ep.id)} variant="outline" size="sm" className="border-cap-error/30 text-cap-error hover:bg-cap-error/10" data-testid="delete-position-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                    </Button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-8 py-5 flex flex-col gap-5">
                  {/* 4 KPI cards — fixed */}
                  <div className="grid grid-cols-4 gap-4 shrink-0" data-testid="pos-modal-kpis">
                    {[
                      { l: 'PRU', v: fmtD(ep.price), c: 'text-white' },
                      { l: 'Valeur actuelle', v: fmt(ep.value), c: 'text-white' },
                      { l: 'Perf €', v: `${ep.perfEuro >= 0 ? '+' : ''}${fmt(ep.perfEuro)}`, c: ep.perfEuro >= 0 ? 'text-cap-emerald' : 'text-cap-error' },
                      { l: 'Perf %', v: fmtP(ep.perfPct), c: ep.perfPct >= 0 ? 'text-cap-emerald' : 'text-cap-error' },
                    ].map(k => (
                      <div key={k.l} className="p-4 bg-cap-bg rounded-xl">
                        <p className="text-cap-text-muted text-xs mb-1">{k.l}</p>
                        <p className={`font-display text-xl font-bold ${k.c}`}>{k.v}</p>
                      </div>
                    ))}
                  </div>

                  {/* Historical chart — fixed */}
                  <div className="bg-cap-bg rounded-xl p-5 shrink-0" data-testid="history-chart">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold">Historique du cours</h3>
                      <div className="flex gap-1">
                        {['YTD', '1A', '3A', '5A', 'MAX'].map(p => (
                          <button key={p} onClick={() => setHistPeriod(p)} className={`px-3 py-1 rounded text-xs font-medium transition-colors ${histPeriod === p ? 'bg-cap-emerald text-cap-bg' : 'bg-cap-bg-elevated text-cap-text-muted hover:text-white'}`} data-testid={`hist-period-${p}`}>{p}</button>
                        ))}
                      </div>
                    </div>
                    {historyLoading ? (
                      <div className="flex items-center justify-center h-48 text-cap-text-muted text-sm"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Chargement...</div>
                    ) : filteredHistory.length > 0 ? (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={filteredHistory}>
                            <defs><linearGradient id="histGradM" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4f8eff" stopOpacity={0.15} /><stop offset="95%" stopColor="#4f8eff" stopOpacity={0} /></linearGradient></defs>
                            <XAxis dataKey="date" stroke="#64748b" fontSize={10} interval="preserveStartEnd" tickFormatter={d => d.slice(5)} />
                            <YAxis stroke="#64748b" fontSize={10} domain={[yMin, yMax]} tickFormatter={v => `${v.toFixed(0)}`} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                              formatter={v => fmtD(v)} labelFormatter={l => l}
                            />
                            <Area type="monotone" dataKey="close" stroke="#4f8eff" fill="url(#histGradM)" strokeWidth={2} name="Cours" dot={false} />
                            {/* PRU line — always visible thanks to yDomain */}
                            <ReferenceLine y={ep.price} stroke="#f97316" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `PRU : ${fmtD(ep.price)}`, position: 'right', fill: '#f97316', fontSize: 10, fontWeight: 600 }} />
                            {/* Transaction markers — triangles ▲▼ with tooltip */}
                            {txOnChart.map((tx, i) => (
                              <ReferenceDot
                                key={i} x={tx.chartDate} y={tx.chartY} r={0}
                                shape={({ cx, cy }) => {
                                  const isBuy = tx.type === 'buy';
                                  const fill = isBuy ? '#00e5a0' : '#ef4444';
                                  const d = isBuy
                                    ? `M ${cx} ${cy - 9} L ${cx + 7} ${cy + 4} L ${cx - 7} ${cy + 4} Z`
                                    : `M ${cx} ${cy + 9} L ${cx + 7} ${cy - 4} L ${cx - 7} ${cy - 4} Z`;
                                  const label = `${isBuy ? 'ACHAT' : 'VENTE'} — ${tx.quantity} × ${fmtD(tx.price)} — ${tx.date.split('-').reverse().join('/')}`;
                                  return <path d={d} fill={fill} stroke="#0d0f17" strokeWidth={1}><title>{label}</title></path>;
                                }}
                              />
                            ))}
                          </ComposedChart>
                        </ResponsiveContainer>
                        <div className="flex items-center justify-center gap-5 mt-1.5 text-xs text-cap-text-muted">
                          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#4f8eff]" />Cours</span>
                          <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-[#f97316]" />PRU</span>
                          <span className="flex items-center gap-1.5"><span className="text-cap-emerald text-[10px] leading-none">&#9650;</span>Achat</span>
                          <span className="flex items-center gap-1.5"><span className="text-cap-error text-[10px] leading-none">&#9660;</span>Vente</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-cap-text-muted text-sm text-center py-8">{ep.ticker ? 'Aucune donnée historique disponible' : 'Ajoutez un ticker pour voir l\'historique'}</p>
                    )}
                  </div>

                  {/* Transactions */}
                  <div className="bg-cap-bg rounded-xl p-5" data-testid="transactions-section">
                    <div className="flex items-center justify-between mb-3 shrink-0">
                      <h3 className="text-white font-semibold">Transactions</h3>
                      <Dialog open={txDlg} onOpenChange={setTxDlg}>
                        <DialogTrigger asChild><Button size="sm" className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30 h-8 text-xs" data-testid="add-tx-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Transaction</Button></DialogTrigger>
                        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-xs">
                          <DialogHeader><DialogTitle className="text-sm">Ajouter une transaction</DialogTitle></DialogHeader>
                          <div className="space-y-3 mt-3">
                            <Select value={txForm.type} onValueChange={v => setTxForm({ ...txForm, type: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white text-sm"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10"><SelectItem value="buy">Achat</SelectItem><SelectItem value="sell">Vente</SelectItem></SelectContent></Select>
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="text-cap-text-secondary text-xs">Quantité</Label><Input type="number" value={txForm.quantity} onChange={e => setTxForm({ ...txForm, quantity: e.target.value })} className="bg-cap-bg border-white/10 text-white text-sm" step="any" /></div>
                              <div><Label className="text-cap-text-secondary text-xs">Prix (€)</Label><Input type="number" value={txForm.price} onChange={e => setTxForm({ ...txForm, price: e.target.value })} className="bg-cap-bg border-white/10 text-white text-sm" step="0.01" /></div>
                            </div>
                            <div><Label className="text-cap-text-secondary text-xs">Date</Label><Input type="date" lang="fr" value={txForm.date} onChange={e => setTxForm({ ...txForm, date: e.target.value })} className="bg-cap-bg border-white/10 text-white text-sm" /></div>
                            <Button onClick={addTransaction} className="w-full bg-cap-emerald text-cap-bg font-semibold text-sm">Ajouter</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    {txs.length > 0 ? (
                      <div className="relative">
                        <div data-testid="tx-scroll-area">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-cap-bg z-10"><tr className="border-b border-white/10">
                              <th className="text-left text-cap-text-muted font-medium px-3 py-2">Type</th>
                              <th className="text-left text-cap-text-muted font-medium px-3 py-2">Date</th>
                              <th className="text-right text-cap-text-muted font-medium px-3 py-2">Quantité</th>
                              <th className="text-right text-cap-text-muted font-medium px-3 py-2">Prix</th>
                              <th className="text-right text-cap-text-muted font-medium px-3 py-2">Montant</th>
                              <th className="px-2 py-2 w-10"></th>
                            </tr></thead>
                            <tbody>
                              {txs.slice().reverse().map(tx => (
                                <tr key={tx.id} className="border-b border-white/5 group/tx">
                                  <td className="px-3 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded ${tx.type === 'buy' ? 'bg-cap-emerald/10 text-cap-emerald' : 'bg-cap-error/10 text-cap-error'}`}>{tx.type === 'buy' ? 'ACHAT' : 'VENTE'}</span></td>
                                  <td className="px-3 py-2.5 text-cap-text-secondary">{tx.date ? format(new Date(tx.date), 'dd/MM/yyyy') : '—'}</td>
                                  <td className="px-3 py-2.5 text-right text-white">{tx.quantity}</td>
                                  <td className="px-3 py-2.5 text-right text-cap-text-secondary">{fmtD(tx.price)}</td>
                                  <td className="px-3 py-2.5 text-right text-white font-medium">{fmt(tx.amount)}</td>
                                  <td className="px-2 py-2.5 text-right"><button onClick={() => deleteTransaction(ep.id, tx.id)} className="p-1 rounded text-cap-text-muted hover:text-cap-error opacity-0 group-hover/tx:opacity-100 transition-all" data-testid={`delete-tx-${tx.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Shadow indicator when more transactions below */}
                        {txs.length > 4 && <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-cap-bg to-transparent rounded-b-xl" />}
                      </div>
                    ) : <p className="text-cap-text-muted text-center py-4 text-sm">Aucune transaction</p>}
                  </div>

                  {/* Actions — fixed at bottom */}
                  <div className="flex gap-3 shrink-0">
                    <div className="flex-1 p-3 bg-cap-bg rounded-xl">
                      <Label className="text-cap-text-secondary text-xs">Mettre à jour le cours manuellement</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input type="number" defaultValue={ep.currentPrice} className="bg-cap-bg-elevated border-white/10 text-white text-sm" id="manual-price-modal" step="0.01" />
                        <Button size="sm" onClick={() => {
                          const v = document.getElementById('manual-price-modal').value;
                          if (v) { savePrices({ ...priceCache, [ep.ticker || ep.id]: { price: parseFloat(v), change: 0, ts: Date.now() } }); toast.success('Cours mis à jour'); }
                        }} className="bg-cap-blue text-white">OK</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ═══ EDIT POSITION MODAL ═══ */}
      <Dialog open={editPosDlg} onOpenChange={setEditPosDlg}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>Modifier la position</DialogTitle></DialogHeader>
          {editPosForm && (
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-cap-text-secondary">Nom du titre</Label><Input value={editPosForm.name} onChange={e => setEditPosForm({ ...editPosForm, name: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="edit-pos-name" /></div>
                <div className="space-y-2"><Label className="text-cap-text-secondary">Ticker</Label><Input value={editPosForm.ticker} onChange={e => setEditPosForm({ ...editPosForm, ticker: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="edit-pos-ticker" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-cap-text-secondary">Code ISIN</Label><Input value={editPosForm.isin} onChange={e => setEditPosForm({ ...editPosForm, isin: e.target.value })} className="bg-cap-bg border-white/10 text-white" data-testid="edit-pos-isin" /></div>
                <div className="space-y-2"><Label className="text-cap-text-secondary">Type</Label><Select value={editPosForm.type} onValueChange={v => setEditPosForm({ ...editPosForm, type: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{POS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label className="text-cap-text-secondary">Compte associé</Label><Select value={editPosForm.account} onValueChange={v => setEditPosForm({ ...editPosForm, account: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue placeholder="Choisir un compte" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-cap-text-secondary">Géographie</Label><Select value={editPosForm.geo} onValueChange={v => setEditPosForm({ ...editPosForm, geo: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{GEOS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label className="text-cap-text-secondary">Secteur</Label><Select value={editPosForm.sector} onValueChange={v => setEditPosForm({ ...editPosForm, sector: v })}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <p className="text-cap-text-muted text-xs">Le PRU et les transactions ne sont pas modifiables ici — utilisez l'historique des transactions.</p>
              <Button onClick={saveEditPosition} className="w-full bg-cap-emerald text-cap-bg font-semibold" data-testid="save-edit-pos-btn">Enregistrer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="bg-[#1a1d27] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Réinitialiser les données</DialogTitle></DialogHeader>
          <p className="text-cap-text-secondary text-sm mt-2">Êtes-vous sûr de vouloir réinitialiser toutes les données de cette section ? Cette action est irréversible.</p>
          <div className="flex items-center justify-end gap-3 mt-4">
            <Button variant="outline" size="sm" className="border-white/10 text-cap-text-muted hover:text-white" onClick={() => setShowResetConfirm(false)}>Annuler</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={handleResetPatrimoine} data-testid="confirm-reset-patrimoine">Réinitialiser</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatrimoinePage;
